// frontend/src/api/client.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const client = axios.create({ baseURL: API_URL });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── 지속 로그인(세션 재개) — J4 프런트 배선 ──────────────────────────────
// 🔴 withCredentials 는 동일 오리진이면 불필요하다(PROD 는 FRONTEND_URL == BACKEND_URL).
//    진짜 전제는 "둘이 갈라지면 안 된다" — 갈라지면 host-only refresh 쿠키가 안 실려
//    resume 이 영구 401 한다. 오리진 분리는 배포 설정 문제라 이 파일이 고칠 대상이 아니다.
export const SESSION_RESUME_PATH = '/api/session/resume';
export const SESSION_LOGOUT_PATH = '/api/session/logout';
// '로그인 유지'를 이 기기에 줄 수 있는가 — 인증·부작용 없는 GET(판정은 서버가 한다).
export const SESSION_ELIGIBILITY_PATH = '/api/session/eligibility';
// 🔴 세션 호출에는 반드시 상한을 둔다 — axios 기본 timeout 은 0(무제한)이라, 응답이 오지
//    않는 회선에서 부팅이 영영 안 끝나거나(로그인 화면 도달 불가) 로그아웃이 안 끝난다.
const SESSION_TIMEOUT_MS = 5000;
// 로그아웃 표식은 localStorage 에 둔다 — sessionStorage 면 새 탭·재로딩에서 resume 이
// 되살아나 방금 로그아웃한 계정으로 자동 재로그인된다(공용 기기 잔존 세션).
const LOGGED_OUT_KEY = 'gwLoggedOut';
// 비로그인 화면 — 여기서는 resume 을 시도하지 않는다.
const ANON_PATHS = ['/login', '/register', '/verify-email', '/unlock-account',
  '/forgot-password', '/reset-password', '/authorize'];
// 같은 탭의 로그인/로그아웃을 App 의 공유 부팅 상태에 알린다(다른 탭은 storage 이벤트).
export const SESSION_EVENT = 'gw:session-change';

export function notifySessionChange() {
  try { window.dispatchEvent(new Event(SESSION_EVENT)); } catch (e) { /* 무시 */ }
}

export function markLoggedOut() {
  try { localStorage.setItem(LOGGED_OUT_KEY, '1'); } catch (e) { /* 무시 */ }
}

export function clearLoggedOutMark() {
  try { localStorage.removeItem(LOGGED_OUT_KEY); } catch (e) { /* 무시 */ }
}

// 🔴 쿼리스트링이 아니라 **경로**로 판정한다 — `?token=<사용자입력>` 안에 경로 문자열이
//    들어가도 오판하지 않는다.
function requestPath(config) {
  const raw = (config && config.url) || '';
  try {
    return new URL(raw, window.location.origin).pathname;
  } catch (e) {
    return String(raw).split('?')[0];
  }
}

function isSessionPath(config) {
  const p = requestPath(config);
  return p === SESSION_RESUME_PATH || p === SESSION_LOGOUT_PATH
    || p.endsWith(SESSION_RESUME_PATH) || p.endsWith(SESSION_LOGOUT_PATH);
}

// 로그인 화면이거나 방금 로그아웃한 상태면 resume 을 시도하지 않는다 — 로그아웃 직후의
// resume 은 AppSidebar 의 탭 간 로그아웃 전파와 싸워 로그아웃을 되살린다.
export function shouldTryResume() {
  try {
    if (localStorage.getItem(LOGGED_OUT_KEY)) return false;
  } catch (e) { /* 접근 불가면 시도는 해 본다 */ }
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  return !ANON_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

// 🔴 401 이어도 resume 을 시도하지 않는 인증 경로 — 접두 `/api/auth/` 전체가 아니다.
//    공용 폰에서 비밀번호를 틀린 사람이 앞사람의 refresh 쿠키로 앞사람 계정에 들어가면 안 된다.
// 🔴 `/api/auth/me` 는 여기 없다(= resume 대상) — 앱 곳곳이 부르는 경로라 만료 직후 이것이
//    먼저 401 을 받으면, 쿠키가 살아 있는데도 /login 으로 튕기고 /login 은 익명 경로라
//    부팅 resume 도 돌지 않는다(= 모바일 자동 로그인이 통째로 무의미해진다).
const NO_RESUME_PATHS = [
  '/api/auth/login', '/api/auth/verify-2fa', '/api/auth/refresh', '/api/auth/register',
  '/api/auth/verify-email', '/api/auth/forgot-password', '/api/auth/reset-password',
  '/api/auth/unlock-account', '/api/auth/me/change-password',
];

function isNoResumePath(config) {
  const p = requestPath(config);
  return NO_RESUME_PATHS.some((x) => p === x || p.endsWith(x));
}

// 🔴 백그라운드 신호(사용자 조작 없이 주기로 나가는 요청 — 기기 활동 하트비트)의 401 은 로그아웃 사유가 아니다.
//    resume 도 /login 이동도 하지 않고 오류만 돌려준다(다음 주기가 그때 토큰으로 다시 보낸다). 목으로 안 막은
//    e2e(test_meetings_full.py 의 401 폴백)에서 입력 한 번에 /login 으로 튕기던 것 방지(collabUnreadStore 의 raw fetch 와 같은 이유).
const QUIET_401 = '_gwQuiet401';

// 요청에 실렸던 access 토큰. 헤더는 AxiosHeaders 일 수도, 평범한 객체일 수도 있다.
function bearerOf(config) {
  const headers = (config && config.headers) || {};
  const raw = (typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization)
    || headers.authorization || '';
  return String(raw).replace(/^Bearer\s+/i, '');
}

// 🔴 sid 클레임이 있는 토큰 = 모바일 지속 세션(쿠키로 되살아날 수 있다). sid 가 없는 PC·옛
//    토큰은 resume 으로 살아날 길이 없으니 예전처럼 곧장 로그인 화면으로 보낸다.
function hasSid(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return false;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.sid !== undefined && claims.sid !== null;
  } catch (e) {
    return false;
  }
}

function clearSessionAndGoLogin() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  notifySessionChange();
  // 🔴 이미 로그인·가입 같은 익명 화면이면 이동하지 않는다 — 같은 주소로의 전체 새로고침이
  //    화면이 띄우려던 오류 문구(비밀번호·인증 코드 틀림)와 2FA 단계를 지운다.
  //    판정은 shouldTryResume() 과 같은 ANON_PATHS 식이다(저장소 지우기는 위에서 이미 했다).
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  if (ANON_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) return;
  window.location.href = '/login';
}

// 실패한 요청을 새 토큰으로 딱 한 번 다시 보낸다(responseType·params 등 원 설정 그대로).
function retryWithToken(config, token) {
  config._gwRetried = true;
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return client.request(config);
}

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    // NOTE: fires on EVERY 401 — including non-auth-critical requests (e.g. a background
    // notification poll, or an endpoint an e2e mock forgot to stub). A single unexpected 401
    // therefore bounces the whole SPA to /login. Keep e2e API route mocks exhaustive so a stray
    // 401 doesn't masquerade as a logout/redirect (see tests/e2e/test_meetings_full.py::test_b8).
    // 🔴 세션 엔드포인트(resume·logout)의 401 은 예외다 — resume 401 은 "저장된 세션이
    //    없다"는 정상 응답이라 여기서 /login 으로 튕기면 부팅마다 재로딩 루프가 되고,
    //    logout 401 은 호출부가 이미 /login 으로 보내는 중이라 하드 내비게이션이 임베드
    //    (MM·NC) 세션 종료 요청을 취소시킨다. QUIET_401 표시 요청(백그라운드 신호)도 예외다.
    // 🔴 401 에서만 세션을 지운다 — 429·5xx·네트워크 오류는 로그아웃 사유가 아니다.
    const config = err.config;
    if (err.response?.status !== 401 || !config || isSessionPath(config) || config[QUIET_401]) return Promise.reject(err);
    // 🔴 인증 경로(로그인·2FA·refresh·비밀번호)의 401 은 예전 그대로다 — 여기서 resume 하면
    //    공용 폰에서 비밀번호를 틀린 사람이 앞사람 계정으로 들어간다. 이미 한 번 재시도한
    //    요청과 로그인 화면·로그아웃 직후도 같다(재시도는 요청당 1회 = 무한 루프 차단).
    if (isNoResumePath(config) || config._gwRetried || !shouldTryResume()) {
      clearSessionAndGoLogin();
      return Promise.reject(err);
    }
    const failed = bearerOf(config);
    const current = localStorage.getItem('token');
    if (!current) {                       // 다른 탭이 로그아웃했다
      clearSessionAndGoLogin();
      return Promise.reject(err);
    }
    // 다른 탭·다른 요청이 이미 토큰을 바꿔 뒀다 — resume 없이 그 토큰으로 한 번 더.
    if (current !== failed) return retryWithToken(config, current);
    const out = await resumeSession();
    if (out.ok) return retryWithToken(config, out.token);
    // 🔴 sid 없는 토큰(PC·옛 토큰)은 쿠키로 살아날 길이 없다 — 비확정 실패도 로그아웃이다
    //    (유예를 주면 503 때 만료된 토큰을 쥔 채 인증 화면에 무기한 머문다).
    if (out.definitive || !hasSid(failed)) {
      clearSessionAndGoLogin();
      return Promise.reject(err);
    }
    // sid 토큰 + 비확정 실패(503·429·네트워크) — 토큰을 남긴다(다음 요청이 다시 시도한다).
    return Promise.reject(err);
  }
);

// 터치 화면인가 → resume·eligibility 의 `?touch=`. 🔴 iPadOS 13+ 는 데스크톱 UA 를 보내
// 이 힌트가 없으면 Mac 과 구별이 안 돼 로그인 유지를 못 받는다(services/device_class.py).
export function touchHint() {
  try {
    return (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) ? 1 : 0;
  } catch (e) {
    return 0;
  }
}

export const sessionAPI = {
  // 기억된 refresh 쿠키로 access 토큰 재발급(자동 로그인). 401 = 저장된 세션 없음(정상).
  // 🔴 `?touch=` 를 반드시 싣는다 — 서버는 모바일 family 에 pc 요청이 오면 토큰을 소비하지
  //    않고 401 한다(session.py). 힌트가 없으면 iPad(데스크톱 UA)는 resume 이 매번 거절된다.
  resume: () => client.post(`${SESSION_RESUME_PATH}?touch=${touchHint()}`, null, { timeout: SESSION_TIMEOUT_MS }),
  // 이 기기에 '로그인 유지'를 보여줄지(로그인 화면 체크박스) — 인증·부작용 없는 GET.
  eligibility: () => client.get(`${SESSION_ELIGIBILITY_PATH}?touch=${touchHint()}`, { timeout: SESSION_TIMEOUT_MS }),
  // 서버 세션 family 폐기 + 쿠키 삭제. 멱등이라 쿠키가 없어도 200 이다
  // (backend/routers/session.py:237-261 — 그래서 revoked=false 는 '실패'가 아니다).
  logout: () => client.post(SESSION_LOGOUT_PATH, null, { timeout: SESSION_TIMEOUT_MS }),
};

// ── 탭 안 단일 비행 resume ───────────────────────────────────────────────
// 🔴 인터셉터·부팅·keepalive 가 동시에 불러도 서버 회전은 한 번이다 — refresh 토큰은 회전형
//    이라 같은 쿠키로 두 번 부르면 한쪽이 '재사용 탐지'에 걸려 family 가 통째로 폐기된다.
let resumeInflight = null;

async function runResume() {
  try {
    const res = await sessionAPI.resume();
    const data = (res && res.data) || {};
    if (!data.access_token) return { ok: false, definitive: false, code: '' };
    // 🔴 저장 직전에 다시 본다 — 응답을 기다리는 사이 다른 탭이 로그아웃했을 수 있다
    //    (여기서 저장하면 방금 로그아웃한 계정으로 되살아난다).
    if (!shouldTryResume()) return { ok: false, definitive: true, code: 'logged_out' };
    localStorage.setItem('token', data.access_token);
    // 응답에 user 가 없으면 이전 계정 캐시를 물려 쓰지 않는다(메뉴·관리자 게이트 오염 방지).
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    else localStorage.removeItem('user');
    notifySessionChange();
    return { ok: true, definitive: false, code: '', token: data.access_token };
  } catch (e) {
    const status = e && e.response && e.response.status;
    // 🔴 거절 본문은 {code, message} 최상위다(session.py `_json`) — detail 아래가 아니다.
    //    detail.code 로 읽으면 전부 undefined 라 모든 거절이 '비확정'이 되어 로그아웃이 안 된다.
    const code = (e && e.response && e.response.data && e.response.data.code) || '';
    // 확정 = 다시 로그인해야 하는 실패. 401(단 session_retry_exhausted 는 제외 — family 는
    // 살아 있고 다른 탭이 새 쿠키를 심었을 수 있다)·403(origin_rejected = 배포 설정 문제).
    const definitive = (status === 401 && code !== 'session_retry_exhausted') || status === 403;
    return { ok: false, definitive, code };
  }
}

// 반환: {ok:true, token} | {ok:false, definitive, code}. 🔴 sid 없는 토큰을 쥔 호출자는
//    ok 가 아니면 전부 확정으로 다룬다(PC 는 쿠키로 살아날 길이 없다 — 인터셉터 참조).
export function resumeSession() {
  if (!shouldTryResume()) return Promise.resolve({ ok: false, definitive: true, code: 'not_eligible' });
  if (!resumeInflight) {
    resumeInflight = runResume();
    const done = () => { resumeInflight = null; };
    resumeInflight.then(done, done);
  }
  return resumeInflight;
}

export const healthAPI = {
  build: () => client.get('/api/health/build'),
};

export const authAPI = {
  register:        (data)  => client.post('/api/auth/register', data),
  login:           (data)  => client.post('/api/auth/login', data),
  verify2fa:       (data)  => client.post('/api/auth/verify-2fa', data),
  // 활동 중인 사용자의 세션 연장. 🔴 사용자 조작이 있었을 때만 부른다
  // (아무 요청에나 연장하면 알림 폴링만으로 세션이 영원히 유지된다) — useSessionKeepAlive 참조
  refresh:         ()      => client.post('/api/auth/refresh'),
  verifyEmail:     (token) => client.get(`/api/auth/verify-email?token=${token}`),
  forgotPassword:  (data)  => client.post('/api/auth/forgot-password', data),
  resetPassword:   (data)  => client.post('/api/auth/reset-password', data),
  getMe:           ()      => client.get('/api/auth/me'),
  updateMe:        (data)  => client.patch('/api/auth/me', data),
  changePassword:  (data)  => client.post('/api/auth/me/change-password', data),
  unlockAccount:   (token) => client.get(`/api/auth/unlock-account?token=${token}`),
};

// 메일(Roundcube) AI 요약·답장 초안 개인 지침 — 내 정보 «메일 AI 지침» 카드.
export const mailAiAPI = {
  getGuide:    ()     => client.get('/api/mail-ai/guide'),
  updateGuide: (data) => client.put('/api/mail-ai/guide', data),
};

export const chatAPI = {
  send:         (data)     => client.post('/api/chat/', { ...(data || {}), reasoning: !!data?.reasoning }),
  // 백그라운드 생성: 연결을 끊는 것만으로는 서버가 안 멈춘다. 중지는 이 엔드포인트로.
  stopRun:      (runId)    => client.post('/api/chat/stop', { run_id: runId }),
  listRuns:     (convId)   => client.get('/api/chat/runs', { params: convId ? { conversation_id: convId } : {} }),
  uploadFile:   (formData) => client.post('/api/chat/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  requestLimit:         (reason, kind) => client.post('/api/chat/limit-request', { reason, kind: kind || 'token' }),
  getLimitRequestStatus: (kind)  => client.get('/api/chat/limit-request', { params: kind ? { kind } : {} }),
  getModels:            ()       => client.get('/api/chat/models'),
  getMyUsage:            ()      => client.get('/api/chat/usage'),
  getNotifications:      (params) => client.get('/api/chat/notifications', { params: params || {} }),
  getUnreadNotificationCount: () => client.get('/api/chat/notifications/unread-count'),
  readNotification:      (id)    => client.post(`/api/chat/notifications/${id}/read`),
  readAllNotifications:  ()      => client.post('/api/chat/notifications/read-all'),
  getAnnouncements:      ()      => client.get('/api/chat/announcements'),
  editMessage:          (id, data) => client.patch(`/api/chat/messages/${id}`, data),
  deleteMessage:        (id)     => client.delete(`/api/chat/messages/${id}`),
  exportDocx:           (data)  => client.post('/api/chat/export-docx', data, { responseType: 'blob' }),
  downloadGenerated:    (fileId) => client.get(`/api/chat/generated/${fileId}`, { responseType: 'blob' }),
  // 연결이 끊겨 다운로드 카드를 못 받은 파일을 나중에 회수하는 목록
  listGenerated:        (params) => client.get('/api/chat/generated', { params }),
  downloadUpload:       (fileId) => client.get(`/api/chat/files/${fileId}/download`, { responseType: 'blob' }),
  // 드라이브 폴더 첨부 — 서버가 폴더를 훑어 {path, name, file_count, truncated} 만 돌려준다(파일은 안 내려받는다)
  previewDriveFolder:   (path)   => client.post('/api/chat/drive-folders/preview', { path }),
  // 코워크 작업공간(프로젝트 폴더 파일)
  workspaceList:     (pid)            => client.get(`/api/chat/workspace/${pid}/files`),
  workspaceUpload:   (pid, formData)  => client.post(`/api/chat/workspace/${pid}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  workspaceDownload: (pid, name)      => client.get(`/api/chat/workspace/${pid}/download`, { params: { name }, responseType: 'blob' }),
  workspaceDelete:   (pid, name)      => client.delete(`/api/chat/workspace/${pid}/file`, { params: { name } }),
};

export const meetingAPI = {
  listFolders:   ()           => client.get('/api/meetings/folders'),   // 트리 순서(DFS) 평면 배열
  // 하위 폴더는 { name, parent_id } 로 보낸다(1단계는 예전처럼 { name }).
  createFolder:  (data)       => client.post('/api/meetings/folders', data),
  updateFolder:  (id, data)   => client.patch(`/api/meetings/folders/${id}`, data),
  deleteFolder:  (id)         => client.delete(`/api/meetings/folders/${id}`),
  // 폴더 위치 옮기기 — parentId=null 이면 최상위. opts.dryRun 이면 검사·share_change 만(값 무변경).
  moveFolder:    (id, parentId, opts = {}) => client.patch(`/api/meetings/folders/${id}/parent`,
    { parent_id: parentId ?? null, ...(opts && opts.dryRun ? { dry_run: true } : {}) }),
  // 삭제 확인창용 개수 {folder_count(하위), note_count, other_note_count, my_note_count}
  folderDeletePreview: (id)   => client.get(`/api/meetings/folders/${id}/delete-preview`),
  folderTrash:   ()           => client.get('/api/meetings/folders/trash'),   // 삭제 묶음의 맨 위 폴더만
  restoreFolder: (id)         => client.post(`/api/meetings/folders/${id}/restore`),
  // 목록 — 인자 객체({folderId, scope, sort, order, date, q, subtree})에서 값이 있는 것만 params 로 싣는다.
  // 🔴 scope('all'=「전체 노트」)와 folder_id 는 함께 보내지 않는다(백엔드가 400 을 낸다).
  // date('YYYY-MM-DD' · KST 날짜)는 scope 와 함께 보낼 수 있다 — 서버가 기존 범위에 더한다(홈 날짜별 노트).
  // q(검색어 · 제목·전사·요약·키워드·폴더명)도 서버가 기존 범위에 더한다 — 빈 값이면 싣지 않는다.
  // subtree(하위 폴더 포함)는 folderId 와 함께일 때만 싣는다(서버는 folder_id 없는 subtree 에 400).
  list:          (opts = {})  => {
    const { folderId, scope, sort, order, date, q, subtree } = opts || {};
    const params = {};
    if (scope) params.scope = scope;
    else if (folderId) params.folder_id = folderId;
    if (subtree && !scope && folderId) params.subtree = 'true';
    if (sort) params.sort = sort;
    if (order) params.order = order;
    if (date) params.date = date;
    if (q) params.q = q;
    return client.get('/api/meetings', { params });
  },
  stats:         ()           => client.get('/api/meetings/stats'),   // 왼쪽 메뉴 개수(all·unfiled·folders)
  // 홈 달력 — month='YYYY-MM'(KST) → {month, days: {'YYYY-MM-DD': 개수}} (노트가 있는 날만)
  calendar:      (month)      => client.get('/api/meetings/calendar', { params: { month } }),
  get:           (id)         => client.get(`/api/meetings/${id}`),
  upload:        (formData, onUploadProgress) => client.post('/api/meetings/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress }),
  remove:        (id)         => client.delete(`/api/meetings/${id}`),
  downloadAudio: (id)         => client.get(`/api/meetings/${id}/audio`, { responseType: 'blob' }),
  downloadTranscript: (id)    => client.get(`/api/meetings/${id}/transcript.txt`, { responseType: 'blob' }),
  downloadSummaryDocx: (id)   => client.get(`/api/meetings/${id}/summary.docx`, { responseType: 'blob' }),
  // 요약·메모 DOCX — 본문(마크다운)은 화면이 만든다(summaryNotesText.js). 채팅 export-docx 는 채팅 권한 게이트라 쓰지 않는다.
  downloadSummaryNotesDocx: (id, content) => client.post(`/api/meetings/${id}/summary-notes.docx`, { content }, { responseType: 'blob' }),
  transcribe:    (id)         => client.post(`/api/meetings/${id}/transcribe`),
  cancelTranscribe: (id)      => client.post(`/api/meetings/${id}/cancel`),   // #141 STT 전사 중지
  setRagOpt:     (id, enabled) => client.post(`/api/meetings/${id}/rag-opt`, { enabled }),
  summarize:     (id, data)   => client.post(`/api/meetings/${id}/summarize`, data),
  update:        (id, data)   => client.patch(`/api/meetings/${id}`, data),
  // P3: 공유·사용자검색·이동
  searchUsers:   (q)          => client.get('/api/meetings/users/search', { params: { q } }),
  listShares:    (fid)        => client.get(`/api/meetings/folders/${fid}/shares`),
  addShare:      (fid, data)  => client.post(`/api/meetings/folders/${fid}/shares`, data),
  removeShare:   (fid, uid)   => client.delete(`/api/meetings/folders/${fid}/shares/${uid}`),
  move:          (id, folderId) => client.patch(`/api/meetings/${id}/folder`, { folder_id: folderId }),
  // 여러 개 처리(한 번에 최대 200건 — 서버 BULK_MAX) — 서버는 '전부 아니면 전무'로 처리하고
  // 오류 detail 을 문자열로 준다(_bulk_detail). /bulk/* 는 /{id}/… 보다 먼저 정의된 라우트다.
  // 이동 실패는 detail(문자열) + failures([{id,title,status,reason}]) — opts.dryRun 일 때만 dry_run 을 싣는다.
  bulkMove:      (ids, folderId, opts) => client.post('/api/meetings/bulk/move',
    { ids, folder_id: folderId ?? null, ...(opts && opts.dryRun ? { dry_run: true } : {}) }),
  bulkTrash:     (ids)        => client.post('/api/meetings/bulk/trash', { ids }),
  bulkRestore:   (ids)        => client.post('/api/meetings/bulk/restore', { ids }),
  bulkPermanent: (ids)        => client.post('/api/meetings/bulk/permanent', { ids }),
  // P4: 휴지통
  trash:         ()           => client.get('/api/meetings/trash'),
  restore:       (id)         => client.post(`/api/meetings/${id}/restore`),
  permanentDelete: (id)       => client.delete(`/api/meetings/${id}/permanent`),
  adminAudit:    ()           => client.get('/api/meetings/admin/audit'),
  adminUsage:    ()           => client.get('/api/meetings/admin/usage'),
  adminSettings: ()           => client.get('/api/meetings/admin/settings'),
  adminSettingsSave: (data)   => client.put('/api/meetings/admin/settings', data),
};

export const projectAPI = {
  list:       ()             => client.get('/api/projects/'),
  create:     (data)         => client.post('/api/projects/', data),
  update:     (id, data)     => client.patch(`/api/projects/${id}`, data),
  remove:     (id)           => client.delete(`/api/projects/${id}`),
  listFiles:  (id)           => client.get(`/api/projects/${id}/files`),
  uploadFile: (id, formData) => client.post(`/api/projects/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFile: (fid)          => client.delete(`/api/projects/files/${fid}`),
  // 프로젝트 공유(owner 전용) — 백엔드는 처음부터 있었는데 화면 배선이 없어 쓰지 못했다.
  listShares:  (id)                 => client.get(`/api/projects/${id}/shares`),
  addShare:    (id, data)           => client.post(`/api/projects/${id}/shares`, data),
  removeShare: (id, kind, pid)      => client.delete(`/api/projects/${id}/shares/${kind}/${pid}`),
  searchUsers: (q)                  => client.get('/api/projects/users/search', { params: { q } }),
  searchGroups:(q)                  => client.get('/api/projects/groups/search', { params: { q } }),
};

export const inquiryAPI = {
  list:        ()          => client.get('/api/inquiries/'),
  types:       ()          => client.get('/api/inquiries/types'),
  create:      (data)      => client.post('/api/inquiries/', data),
  update:      (id, data)  => client.patch(`/api/inquiries/${id}`, data),
  remove:      (id)        => client.delete(`/api/inquiries/${id}`),
  uploadFile:  (formData)  => client.post('/api/inquiries/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadFile:(fileId)    => client.get(`/api/inquiries/files/${fileId}/download`, { responseType: 'blob' }),
  // 관리자
  adminList:   (status)    => client.get('/api/inquiries/admin/all', { params: status ? { status } : {} }),
  adminAnswer: (id, data)  => client.post(`/api/inquiries/admin/${id}/answer`, data),
  adminRemove: (id)        => client.delete(`/api/inquiries/admin/${id}`),
};

// 메인 대시보드 + 개인 투두
export const dashboardAPI = {
  get:           () => client.get('/api/dashboard'),
  driveRecent:   () => client.get('/api/dashboard/drive-recent'),
  calendar:      () => client.get('/api/dashboard/calendar'),
  // 홈(시안 F) — 지표 4칸 · 오늘 일정(KST 오늘 하루 · 시각순 · 최대 6건 + more)
  homeMetrics:   () => client.get('/api/dashboard/home-metrics'),
  calendarToday: () => client.get('/api/dashboard/calendar', { params: { range: 'today' } }),
  unreadSummary: () => client.get('/api/dashboard/unread-summary'),
  mail:          () => client.get('/api/dashboard/mail'),
  mailCredStatus:() => client.get('/api/dashboard/mail-credential'),
  mailCredSet:   (password) => client.put('/api/dashboard/mail-credential', { password }),
  // 모닝 브리핑(③)
  morningBrief:        () => client.get('/api/dashboard/morning-brief'),
  morningBriefRefresh: () => client.post('/api/dashboard/morning-brief/refresh'),
  morningBriefSettings:() => client.get('/api/dashboard/morning-brief/settings'),
  setMorningBrief:     (enabled) => client.put('/api/dashboard/morning-brief/settings', { enabled }),
};
// 오피스 빈 문서 추적·자동삭제
export const officeAPI = {
  track:   (data) => client.post('/api/office/track', data),
  cleanup: ()     => client.post('/api/office/cleanup'),
};
// 사내 주소록 + 조직도(다우 동기화)
export const directoryAPI = {
  employees:   (q = '', org = '') => client.get('/api/directory/employees', { params: { q, org } }),
  departments: () => client.get('/api/directory/departments'),
  status:      () => client.get('/api/directory/status'),
  sync:        () => client.post('/api/directory/sync'),
  scheduleGet: () => client.get('/api/directory/schedule'),
  scheduleSet: (cfg) => client.put('/api/directory/schedule', cfg),
};
export const todoAPI = {
  list:   ()        => client.get('/api/todos'),
  create: (text)    => client.post('/api/todos', { text }),
  update: (id, d)   => client.patch(`/api/todos/${id}`, d),
  remove: (id)      => client.delete(`/api/todos/${id}`),
};

export const promptAPI = {
  list:   ()       => client.get('/api/prompts'),
  create: (d)      => client.post('/api/prompts', d),
  update: (id, d)  => client.patch(`/api/prompts/${id}`, d),
  remove: (id)     => client.delete(`/api/prompts/${id}`),
};

// 사내게시판(현재 사이드바 미노출, 선개발 — /board 직접 접근으로 미리보기)
export const boardAPI = {
  list:          (params)      => client.get('/api/board', { params: params || {} }),
  get:           (id)          => client.get(`/api/board/${id}`),
  create:        (data)        => client.post('/api/board', data),
  update:        (id, data)    => client.patch(`/api/board/${id}`, data),
  remove:        (id)          => client.delete(`/api/board/${id}`),
  addComment:    (id, content) => client.post(`/api/board/${id}/comments`, { content }),
  removeComment: (cid)         => client.delete(`/api/board/comments/${cid}`),
};

export const execMeetingAPI = {
  ping:        ()         => client.get('/api/exec-meeting/ping'),
  weeks:       ()         => client.get('/api/exec-meeting/weeks'),
  list:        (week)     => client.get('/api/exec-meeting/reports', { params: { week } }),
  get:         (id)       => client.get(`/api/exec-meeting/reports/${id}`),
  create:      (data)     => client.post('/api/exec-meeting/reports', data),
  update:      (id, data) => client.put(`/api/exec-meeting/reports/${id}`, data),
  remove:      (id)       => client.delete(`/api/exec-meeting/reports/${id}`),
  uploadFile:  (form)     => client.post('/api/exec-meeting/files', form),
  downloadFile:(id)       => client.get(`/api/exec-meeting/files/${id}/download`, { responseType: 'blob' }),
  uploadImage: (form)     => client.post('/api/exec-meeting/img', form),
  image:       (uuid)     => client.get(`/api/exec-meeting/img/${uuid}`, { responseType: 'blob' }),
  // 부서 목록(사용 중인 것) — 제출 탭 선택지·통합 탭 제출 현황(#3130 · 임원회의 열람권)
  departments: ()         => client.get('/api/exec-meeting/departments'),
  // 통합 탭 검색(#3130 4번) — 주차·날짜면 {weeks}, 본문 낱말이면 {hits}. 검색어는 params 로만.
  search:      (q)        => client.get('/api/exec-meeting/search', { params: { q } }),
  // 관리자 탭(#3130 · 임원회의 관리 권한) — 부서 관리 + 이 메뉴 접근권한 부여·회수
  admin: {
    departments:      ()          => client.get('/api/exec-meeting/admin/departments'),
    orgDepartments:   ()          => client.get('/api/exec-meeting/admin/org-departments'),
    addDepartment:    (data)      => client.post('/api/exec-meeting/admin/departments', data),
    updateDepartment: (id, data)  => client.patch(`/api/exec-meeting/admin/departments/${id}`, data),
    removeDepartment: (id)        => client.delete(`/api/exec-meeting/admin/departments/${id}`),
    users:            (q)         => client.get('/api/exec-meeting/admin/access', { params: { q: q || '' } }),
    setAccess:        (id, granted) => client.put(`/api/exec-meeting/admin/access/${id}`, { granted: !!granted }),
    // PDF 가져오기(#3130 2번) — 옛 통합 주간 보고서 PDF → 초안(서버가 읽음) → 미리보기 → 고른 카드만 확정
    importList:       ()          => client.get('/api/exec-meeting/admin/import'),
    importStart:      (form)      => client.post('/api/exec-meeting/admin/import', form),
    importGet:        (id)        => client.get(`/api/exec-meeting/admin/import/${id}`),
    importTaken:      (id, weekKey) => client.get(`/api/exec-meeting/admin/import/${id}/taken`, { params: { week_key: weekKey || '' } }),
    importCommit:     (id, data)  => client.post(`/api/exec-meeting/admin/import/${id}/commit`, data),
    importCancel:     (id)        => client.delete(`/api/exec-meeting/admin/import/${id}`),
  },
};

// CS 티켓(현재 사이드바 미노출, 선개발 — /tickets 직접 접근으로 미리보기)
export const ticketAPI = {
  list:   (params) => client.get('/api/tickets', { params: params || {} }),
  stats:  ()       => client.get('/api/tickets/stats'),
  get:    (id)     => client.get(`/api/tickets/${id}`),
  create: (data)   => client.post('/api/tickets', data),
  update: (id, d)  => client.patch(`/api/tickets/${id}`, d),
  remove: (id)     => client.delete(`/api/tickets/${id}`),
  getConfig: ()    => client.get('/api/tickets/config'),
  setConfig: (d)   => client.put('/api/tickets/config', d),
  handoff: (id)    => client.post(`/api/tickets/${id}/handoff`),
  aiAssist:(id)    => client.post(`/api/tickets/${id}/ai-assist`),
  reply:   (id, text) => client.post(`/api/tickets/${id}/reply`, { text }),
  mmFile:  (id, fileId) => client.get(`/api/tickets/${id}/mm-files/${fileId}`, { responseType: 'blob' }),
  // 메신저 /cs 로 들어온 **모든 문의**(해결·무반응 포함). 티켓 목록엔 접수된 것만 올라온다.
  intakes: (params) => client.get('/api/tickets/intakes', { params: params || {} }),
};

export const dxBoardAPI = {
  get: (refresh = false) => client.get('/api/dx-board', { params: refresh ? { refresh: 1 } : {} }),
  getWeekly: (offset = 0) => client.get('/api/dx-board/weekly', { params: { week_offset: offset } }),
  getSummary: () => client.get('/api/dx-board/summary'),
  getAnalytics: () => client.get('/api/dx-board/analytics'),
  getOverdue: () => client.get('/api/dx-board/overdue'),
  syncAnalytics: () => client.post('/api/dx-board/analytics/sync'),
  generateSummary: () => client.post('/api/dx-board/summary/generate'),
};

export const supportBoardAPI = {
  list: () => client.get('/api/support-board'),
  create: (data) => client.post('/api/support-board', data),
  update: (id, data) => client.patch(`/api/support-board/${id}`, data),
  remove: (id) => client.delete(`/api/support-board/${id}`),
  listSchedules: (pid) => client.get(`/api/support-board/${pid}/schedules`),
  createSchedule: (pid, data) => client.post(`/api/support-board/${pid}/schedules`, data),
  updateSchedule: (sid, data) => client.patch(`/api/support-board/schedules/${sid}`, data),
  deleteSchedule: (sid) => client.delete(`/api/support-board/schedules/${sid}`),
  assignees: () => client.get('/api/support-board/assignees'),
};

export const historyAPI = {
  listConversations:  ()              => client.get('/api/history/conversations'),
  listTrash:          ()              => client.get('/api/history/conversations/trash'),
  getMessages:        (convId)        => client.get(`/api/history/conversations/${convId}/messages`),
  updateConversation: (convId, data)  => client.patch(`/api/history/conversations/${convId}`, data),
  deleteConversation: (convId)        => client.delete(`/api/history/conversations/${convId}`),
  restoreConversation:(convId)        => client.post(`/api/history/conversations/${convId}/restore`),
  permanentlyDeleteConversation: (convId) => client.delete(`/api/history/conversations/${convId}/permanent`),
  search:             (q)             => client.get('/api/history/search', { params: { q } }),
};

// 메신저 '읽음 표시' — MM 은 read receipt 기능이 없어 게이트웨이가 채널 열람시각으로 집계한다.
export const mmAPI = {
  readReceipts: (postIds) => client.get('/api/mm/read-receipts', { params: { posts: postIds.join(',') } }),
  // 프로필 팝업에 덧붙일 전화·직급·직책(다우). 메신저 권한 사용자만·대상도 메신저 사용자만.
  profileExtra: (email) => client.get('/api/mm/profile-extra', { params: { email } }),
  // 채널의 스티커 첨부 수 — 화면이 «채널 파일» 개수에서 뺀다(채널 멤버만·숫자 하나만).
  stickerFileCount: (channelId) => client.get('/api/mm/sticker-file-count', { params: { channel_id: channelId } }),
  // 스레드 안읽은 답글 수 — activate=1 은 배지 화면 첫 조회 때 서버 기준선을 확정한다(그 전 답글은 0건).
  threadUnread: (rootIds) => client.get('/api/mm/thread-unread', { params: { roots: rootIds.join(','), activate: 1 } }),
  threadRead: (rootId) => client.post('/api/mm/thread-read', { root_id: rootId }),
};

// 메신저 스티커 피커 — 목록·이미지는 게이트웨이가 내려주고(읽기 전용), '전송'은 사용자
// 브라우저가 본인 MM 세션으로 직접 한다(utils/mmSticker). 🔴 여기에 전송 API 를 만들지 않는다.
export const stickerAPI = {
  manifest: () => client.get('/api/stickers/manifest'),
  // 썸네일 묶음 — 팩당 **요청 1번**(128px PNG data URL). 타일마다 원본을 받으면 38장 = 38요청이라
  // `/api/` 초당 한도 탓에 간격을 둬야 했고 첫 로딩이 10초 가까이 걸렸다.
  thumbs: (pack) => client.get(`/api/stickers/thumbs/${encodeURIComponent(pack)}`),
  // v = 이미지 mtime(manifest 가 준다) — 이미지만 갈아끼워도 브라우저 캐시가 비켜간다.
  image: (pack, id, v) => client.get(`/api/stickers/${encodeURIComponent(pack)}/${encodeURIComponent(id)}`, { params: { v }, responseType: 'blob' }),
};

// 메신저 오피스 미리보기 — 브라우저가 MM 에서 받은 파일을 다시 올려 페이지 PNG 를 받는다(백엔드 P1).
export const officePreviewAPI = {
  create: (blob, fileName) => {
    const fd = new FormData();
    fd.append('file', blob, fileName);
    return client.post('/api/office-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  page: (previewId, n) => client.get(`/api/office-preview/${encodeURIComponent(previewId)}/pages/${n}`, { responseType: 'blob' }),
};

export const searchAPI = {
  search: (q, limit = 5) => client.get('/api/search', { params: { q, limit } }),
};

export const documentAPI = {
  // 사용자
  uploadDocument:   (formData) => client.post('/api/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmDocument:  (data)     => client.post('/api/documents/confirm', data),
  myDocuments:      ()         => client.get('/api/documents/my'),
  groupDocuments:   ()         => client.get('/api/documents/group'),
  deleteDocument:   (id)       => client.delete(`/api/documents/${id}`),

  // 관리자
  adminListDocuments:   (status)     => client.get('/api/admin/documents/', { params: status ? { status } : {} }),
  adminApproveDocument: (id)         => client.patch(`/api/admin/documents/${id}/approve`),
  batchApprove:         (doc_ids)    => client.post('/api/admin/documents/batch-approve', { doc_ids }),
  batchClassify:        (doc_ids)    => client.post('/api/admin/documents/batch-classify', { doc_ids }),
  adminRejectDocument:  (id, reason) => client.patch(`/api/admin/documents/${id}/reject`, { reason }),
  adminDeleteDocument:  (id)         => client.delete(`/api/admin/documents/${id}`),
  bulkDeleteDocuments:  (status)     => client.delete('/api/admin/documents', { params: status ? { status } : {} }),
  rependDocument:       (id)         => client.patch(`/api/admin/documents/${id}/repend`),
  classifyDocument:     (id)         => client.post(`/api/admin/documents/${id}/classify`),
  ragProcessDocument:   (id)         => client.post(`/api/admin/documents/${id}/rag-process`),   // conf_high 내부 처리(OCR·색인)
  adminDocumentContent: (id)         => client.get(`/api/admin/documents/${id}/content`),
  adminDownloadDocument:(id)         => client.get(`/api/admin/documents/${id}/download`, { responseType: 'blob' }),
  collectNotion:        ()           => client.post('/api/admin/documents/collect/notion'),
  collectGoogle:        ()           => client.post('/api/admin/documents/collect/google'),
  collectNc:            ()           => client.post('/api/admin/documents/collect/nc'),
  getNcFolders:         ()           => client.get('/api/admin/nc-folders'),
  saveNcFolders:        (data)       => client.put('/api/admin/nc-folders', data),
  getCollectionSettings:    ()       => client.get('/api/admin/collection-settings'),
  updateCollectionSettings: (data)   => client.put('/api/admin/collection-settings', data),
  getCollectionStatus:  ()           => client.get('/api/admin/collection-status'),
  collectionControl:    (data)       => client.post('/api/admin/collection-control', data),
  getCollectionHistory: ()           => client.get('/api/admin/collection-history'),
};

// 물류 문서 자동화(② OCR→구조화 추출)
export const docExtractAPI = {
  types:    ()              => client.get('/api/doc-extract/types'),
  list:     ()              => client.get('/api/doc-extract'),
  get:      (id)            => client.get(`/api/doc-extract/${id}`),
  create:   (formData)      => client.post('/api/doc-extract', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:   (id, data)      => client.patch(`/api/doc-extract/${id}`, data),
  remove:   (id)            => client.delete(`/api/doc-extract/${id}`),
  excel:    (id)            => client.get(`/api/doc-extract/${id}/excel`, { responseType: 'blob' }),
};

export const adminAPI = {
  // 모닝 브리핑(③) 사용자↔담당화주 매핑
  listPwnMap:    (userId)        => client.get('/api/admin/pwn-map', { params: userId ? { user_id: userId } : {} }),
  // 사내데이터 조회 범위(스코프) 지정용 선택지 — 관리자 전용 구조화 조회
  wmsPwnSearch:  (q, limit = 20)  => client.get('/api/admin/wms/pwn-search', { params: { q: q || '', limit } }),
  wmsDepots:     ()               => client.get('/api/admin/wms/depots'),
  createPwnMap:  (data)          => client.post('/api/admin/pwn-map', data),
  deletePwnMap:  (id)            => client.delete(`/api/admin/pwn-map/${id}`),
  searchPwn:     (q)             => client.get('/api/admin/pwn-search', { params: { q } }),
  // RAG 청크 적재(M1)
  ragChunkStatus:  ()       => client.get('/api/admin/rag/chunks/status'),
  ragImportChunks: (chunks) => client.post('/api/admin/rag/chunks', { chunks }),
  ragDeleteChunks: (docId)  => client.delete(`/api/admin/rag/chunks/${docId}`),
  // 능동형 운영 인사이트 봇(① AI 모니터링)
  opsWatchRules:    ()         => client.get('/api/admin/ops-watch-rules'),
  opsWatchCreate:   (data)     => client.post('/api/admin/ops-watch-rules', data),
  opsWatchUpdate:   (id, data) => client.patch(`/api/admin/ops-watch-rules/${id}`, data),
  opsWatchDelete:   (id)       => client.delete(`/api/admin/ops-watch-rules/${id}`),
  opsWatchEvaluate: (id)       => client.post(`/api/admin/ops-watch-rules/${id}/evaluate`),
  opsWatchAlerts:   ()         => client.get('/api/admin/ops-watch-alerts'),
  opsWatchSnapshot: ()         => client.post('/api/admin/ops-watch-snapshot'),
  reportSchedules:       ()         => client.get('/api/admin/report-schedules'),
  reportScheduleCreate:  (data)     => client.post('/api/admin/report-schedules', data),
  reportScheduleUpdate:  (id, data) => client.patch(`/api/admin/report-schedules/${id}`, data),
  reportScheduleDelete:  (id)       => client.delete(`/api/admin/report-schedules/${id}`),
  reportSchedulePublish: (id)       => client.post(`/api/admin/report-schedules/${id}/publish`),
  // RAG — 문서 접근범위(ACL) + 검색 디버그 (B feat/B-rag-acl·r4)
  adminScopeOrgs:    ()          => client.get('/api/admin/documents/scope-orgs'),
  // 등급·범위는 같은 엔드포인트에서 고친다. 인자를 생략한 쪽은 서버가 건드리지 않는다.
  adminSetDocScopes: (id, scopes, tier) => client.patch(`/api/admin/documents/${id}/scopes`,
    { ...(scopes === undefined ? {} : { access_scopes: scopes }),
      ...(tier === undefined ? {} : { confidential_tier: tier }) }),
  batchSetDocTier: (ids, tier) => client.post('/api/admin/documents/batch-tier',
    { doc_ids: ids, confidential_tier: tier }),
  ragSearch:         (query, top_n) => client.post('/api/admin/rag/search', { query, top_n }),
  // 권한 미리보기 — **대상 사용자의 시야로** 검색한다(위 ragSearch 는 admin 시야=전체라 이 용도로 쓰면 안 된다).
  ragPreviewAs:      (user_id, query, top_n) => client.post('/api/admin/rag/preview-as', { user_id, query, top_n }),
  ragReindexAll:     ()            => client.post('/api/admin/rag/reindex-all'),
  getRagHybrid:      ()            => client.get('/api/admin/rag/hybrid'),
  setRagHybrid:      (body)        => client.put('/api/admin/rag/hybrid', body),   // 보낸 칸만 바뀐다(V4a)
  // RAG 접근권한 외부그룹 (B feat/B-rag-acl-groups)
  listRagGroups:        ()                  => client.get('/api/admin/rag-groups'),
  createRagGroup:       (body)              => client.post('/api/admin/rag-groups', body),
  updateRagGroup:       (id, body)          => client.patch(`/api/admin/rag-groups/${id}`, body),
  deleteRagGroup:       (id)                => client.delete(`/api/admin/rag-groups/${id}`),
  listRagGroupMembers:  (id)                => client.get(`/api/admin/rag-groups/${id}/members`),
  addRagGroupMember:    (id, user_id)       => client.post(`/api/admin/rag-groups/${id}/members`, { user_id }),
  removeRagGroupMember: (id, user_id)       => client.delete(`/api/admin/rag-groups/${id}/members/${user_id}`),
  // 기능(source)별 사용량 카드 (C feat/c-admin-usage-source)
  getUsageBySource:  (start_date, end_date, user_id, group_id) => client.get('/api/admin/usage/by-source', { params: { start_date, end_date, ...(user_id ? { user_id } : {}), ...(group_id ? { group_id } : {}) } }),
  getInternalDataStats: (start_date, end_date) => client.get('/api/admin/internal-data-stats', { params: { start_date, end_date } }),
  getSystemLogStats:    (start_date, end_date) => client.get('/api/admin/system-log-stats', { params: { start_date, end_date } }),
  // 사용자
  listUsers:   ()                 => client.get('/api/admin/users'),
  // RAG 조직 권한(검색범위) — 2026-08-18 확정 정책
  ragScopePreview:      (uid)       => client.get(`/api/admin/rag-scopes/${uid}`),
  ragScopeGrant:        (uid, data) => client.post(`/api/admin/rag-scopes/${uid}`, data),
  ragScopeRevoke:       (uid, scope, mode = 'allow') =>
    client.delete(`/api/admin/rag-scopes/${uid}`, { params: { scope, mode } }),
  ragShipperMapSummary: ()          => client.get('/api/admin/rag-scopes/shipper-map/summary'),
  ragShipperMapRebuild: ()          => client.post('/api/admin/rag-scopes/shipper-map/rebuild'),
  updateUser:  (userId, data)     => client.patch(`/api/admin/users/${userId}`, data),
  deleteUser:  (userId)           => client.delete(`/api/admin/users/${userId}`),
  retireUser:  (userId)           => client.post(`/api/admin/users/${userId}/retire`),
  userSessions:           (userId)    => client.get(`/api/admin/users/${userId}/sessions`),
  revokeSession:           (sessionId) => client.post(`/api/admin/sessions/${sessionId}/revoke`),
  revokeUserSessions:      (userId)    => client.post(`/api/admin/users/${userId}/revoke-sessions`),
  revokeAllMobileSessions: (confirm)   => client.post('/api/admin/sessions/revoke-all-mobile', { confirm }),
  // 회의 녹음 자동 적재(브리지) + CS 티켓 Notion 설정
  listRecordingIngest:  ()        => client.get('/api/admin/recording-ingest'),
  meetingSettings:      ()        => client.get('/api/meetings/admin/settings'),
  meetingSettingsSave:  (data)    => client.put('/api/meetings/admin/settings', data),
  scanRecordingIngest:  ()        => client.post('/api/admin/recording-ingest/scan'),
  getCsNotion:          ()        => client.get('/api/tickets/config'),
  setCsNotion:          (data)    => client.put('/api/tickets/config', data),

  // 그룹
  listGroups:   ()                => client.get('/api/admin/groups'),
  createGroup:  (data)            => client.post('/api/admin/groups', data),
  updateGroup:  (groupId, data)   => client.patch(`/api/admin/groups/${groupId}`, data),
  deleteGroup:  (groupId)         => client.delete(`/api/admin/groups/${groupId}`),

  // 한도 승인 요청
  listLimitRequests: (status)     => client.get('/api/admin/limit-requests', { params: status ? { status } : {} }),
  approveRequest:    (id, data)   => client.post(`/api/admin/limit-requests/${id}/approve`, data),
  rejectRequest:     (id)         => client.post(`/api/admin/limit-requests/${id}/reject`),

  // 사용량 / 접속 로그
  getUsage:      (startDate, endDate, userId, groupId) => client.get('/api/admin/usage', { params: { start_date: startDate, end_date: endDate, ...(userId ? { user_id: userId } : {}), ...(groupId ? { group_id: groupId } : {}) } }),
  getDailyUsage: (year, month, userId, groupId) => client.get('/api/admin/usage/daily', { params: { year, month, user_id: userId || undefined, group_id: groupId || undefined } }),
  getDailyUsageByUser: (year, month, userId, groupId) => client.get('/api/admin/usage/daily-by-user', { params: { year, month, user_id: userId || undefined, group_id: groupId || undefined } }),
  getAccessLogs: (startDate, endDate, limit=300, q) => client.get('/api/admin/access-logs', { params: { start_date: startDate, end_date: endDate, limit, q: q || undefined } }),
  getSystemLogs: (params) => client.get('/api/admin/system-logs', { params }),
  deleteSystemLogs: ()    => client.delete('/api/admin/system-logs'),
  getInternalDataLogs:    (params) => client.get('/api/admin/internal-data-logs', { params }),
  deleteInternalDataLogs: ()       => client.delete('/api/admin/internal-data-logs'),
  // 삭제된 계정의 대화(보존분) — 시스템 관리자만. 본문 열람은 서버에 감사 기록이 남는다.
  listArchivedChats:      (params) => client.get('/api/admin/archived-chats', { params }),
  readArchivedChat:       (id)     => client.get(`/api/admin/archived-chats/${id}`),
  getLogDates:     ()             => client.get('/api/admin/log-files/dates'),
  getLogFile:      (date, params) => client.get(`/api/admin/log-files/${date}`, { params }),
  downloadLogFile: (date, params) => client.get(`/api/admin/log-files/${date}/download`, { params, responseType: 'blob' }),
  getInternalDataTools:   ()       => client.get('/api/admin/internal-data-tools'),
  listCustomTools:   ()           => client.get('/api/admin/custom-query-tools'),
  createCustomTool:  (data)       => client.post('/api/admin/custom-query-tools', data),
  updateCustomTool:  (id, data)   => client.patch(`/api/admin/custom-query-tools/${id}`, data),
  deleteCustomTool:  (id)         => client.delete(`/api/admin/custom-query-tools/${id}`),
  testCustomTool:    (id, args)   => client.post(`/api/admin/custom-query-tools/${id}/test`, { args }),
  aiDraftTool:       (description, target) => client.post('/api/admin/custom-query-tools/ai-draft', { description, target: target || 'v2' }),
  getDbTables:       ()           => client.get('/api/admin/db-schema'),
  getDbTable:        (table)      => client.get(`/api/admin/db-schema/${table}`),
  // 1.0(구 시스템)은 백엔드에 원본 연결이 없다 — **미러 wms_mirror_v1 을 통해서만** 본다.
  getDbTablesV1:     ()           => client.get('/api/admin/db-schema-v1'),
  getDbTableV1:      (table)      => client.get(`/api/admin/db-schema-v1/${table}`),
  getMirrorStatus:   ()           => client.get('/api/admin/mirror-status'),
  // 커버리지는 큰 테이블 MIN/MAX 라 느리다 — 현황과 분리해 화면이 멈추지 않게 한다.
  getMirrorCoverage: ()           => client.get('/api/admin/mirror-status/coverage'),
  getInternalDataLanOnly: ()       => client.get('/api/admin/settings/internal-data-lan-only'),
  setInternalDataLanOnly: (v)      => client.put('/api/admin/settings/internal-data-lan-only', { lan_only: v }),
  getAdhocSql:       ()           => client.get('/api/admin/settings/adhoc-sql'),
  setAdhocSql:       (v)          => client.put('/api/admin/settings/adhoc-sql', { enabled: v }),
  // 협업 그룹폴더(팀/부서 공유폴더)
  listGroupFolders:  ()           => client.get('/api/admin/collab/group-folders'),
  createGroupFolder: (data)       => client.post('/api/admin/collab/group-folders', data),
  deleteGroupFolder: (id)         => client.delete(`/api/admin/collab/group-folders/${id}`),
  addGroupFolderMember:    (gid, user_id) => client.post(`/api/admin/collab/group-folders/${gid}/members`, { user_id }),
  removeGroupFolderMember: (gid, user_id) => client.delete(`/api/admin/collab/group-folders/${gid}/members/${user_id}`),
  getCollabAiName:    ()       => client.get('/api/admin/settings/collab-ai-name'),
  updateCollabAiName: (value)  => client.patch('/api/admin/settings/collab-ai-name', { value }),
  provisionCollab:    (uid, target) => client.post(`/api/admin/collab/provision/${uid}`, { target }),
  createUser:         (data)        => client.post('/api/admin/users', data),
  mmListTeams:    ()        => client.get('/api/admin/mm/teams'),
  mmListChannels: (tid)     => client.get(`/api/admin/mm/teams/${tid}/channels`),
  mmCreateChannel:(team_id, display_name, is_private) => client.post('/api/admin/mm/channels', { team_id, display_name, private: !!is_private }),
  mmDeleteTeam:   (tid)     => client.delete(`/api/admin/mm/teams/${tid}`),
  mmDeleteChannel:(cid)     => client.delete(`/api/admin/mm/channels/${cid}`),
  mmArchiveChannel:(cid)    => client.post(`/api/admin/mm/channels/${cid}/archive`),
  mmRenameChannel:(cid, display_name) => client.patch(`/api/admin/mm/channels/${cid}`, { display_name }),
  getSmtp:                ()       => client.get('/api/admin/settings/smtp'),
  updateSmtp:             (data)   => client.put('/api/admin/settings/smtp', data),
  testSmtp:               (data)   => client.post('/api/admin/settings/smtp/test', data),
  // 테스트 메시지(시스템 관리자 전용) — 사용자 검색 + 통로별 시험 발송(push·inapp·messenger·email)
  searchTestMessageUsers: (q)      => client.get('/api/admin/test-message/users', { params: { q } }),
  sendTestMessage:        (user_id, channels, message) => client.post('/api/admin/test-message', { user_id, channels, message }),
  getTwofaMode:          ()       => client.get('/api/admin/settings/twofa-mode'),
  setTwofaMode:           (mode)   => client.put('/api/admin/settings/twofa-mode', { mode }),

  // 환율(USD→KRW, 일 1회 인터넷 갱신)
  getExchangeRate:     ()  => client.get('/api/admin/exchange-rate'),
  refreshExchangeRate: ()  => client.post('/api/admin/exchange-rate/refresh'),

  // 월 비용 예산
  getBudget:  ()       => client.get('/api/admin/budget'),
  setBudget:  (krw)    => client.put('/api/admin/budget', { budget_krw: krw }),

  // 서버 리소스 모니터링(CPU·메모리·디스크) — 시스템 관리자 전용
  serverMonitorCurrent:  ()      => client.get('/api/admin/server-monitor/current'),
  serverMonitorHistory:  (range) => client.get('/api/admin/server-monitor/history', { params: { range } }),
  serverMonitorSettings: ()      => client.get('/api/admin/server-monitor/settings'),
  serverMonitorSave:     (data)  => client.put('/api/admin/server-monitor/settings', data),
  serverMonitorTest:     ()      => client.post('/api/admin/server-monitor/test-alert'),

  // 운영 배포 이력(기록 파일 읽기만) — 시스템 관리자 전용. 빈 날짜 = 그 끝 제한 없음
  deployHistory: (start, end) => client.get('/api/admin/deploy-history', { params: { start: start || undefined, end: end || undefined } }),

  // 조직 실청구 비용(Anthropic Admin Cost API)
  getOrgCost: (start, end) => client.get('/api/admin/org-cost', { params: { start: start || undefined, end: end || undefined } }),

  // Anthropic 키 쌍 — 시스템 관리자 전용. 쓰기 다섯은 재인증 본문 { current_password, otp } 를 싣는다.
  // 시험만 재인증·본문이 없다(활성을 바꾸지 않는다).
  listAnthropicKeys:        ()         => client.get('/api/admin/anthropic-keys'),
  createAnthropicKeyPair:   (body)     => client.post('/api/admin/anthropic-keys', body),
  importAnthropicEnvKeys:   (body)     => client.post('/api/admin/anthropic-keys/import-env', body),
  testAnthropicKeyPair:     (id)       => client.post(`/api/admin/anthropic-keys/${encodeURIComponent(id)}/test`),
  activateAnthropicKeyPair: (id, body) => client.post(`/api/admin/anthropic-keys/${encodeURIComponent(id)}/activate`, body),
  deactivateAnthropicKeys:  (body)     => client.post('/api/admin/anthropic-keys/deactivate', body),
  // 🔴 본문 실은 DELETE — axios 는 config.data 로 싣는다. 본문이 없으면 재인증에서 403.
  deleteAnthropicKeyPair:   (id, body) => client.delete(`/api/admin/anthropic-keys/${encodeURIComponent(id)}`, { data: body }),

  // 공지사항
  listAnnouncements:   ()          => client.get('/api/admin/announcements'),
  createAnnouncement:  (data)      => client.post('/api/admin/announcements', data),
  updateAnnouncement:  (id, data)  => client.patch(`/api/admin/announcements/${id}`, data),
  deleteAnnouncement:  (id)        => client.delete(`/api/admin/announcements/${id}`),

  // 시스템 프롬프트
  getSystemPrompt:    ()       => client.get('/api/admin/settings/system-prompt'),
  updateSystemPrompt: (value)  => client.patch('/api/admin/settings/system-prompt', { value }),

  // 서비스 가이드(도움말) — 게이트웨이 자체 기능/사용법 안내
  getServiceGuide:    ()       => client.get('/api/admin/settings/service-guide'),
  updateServiceGuide: (value)  => client.patch('/api/admin/settings/service-guide', { value }),

  // 채팅 히스토리 길이
  getChatHistoryLimit: ()       => client.get('/api/admin/settings/chat-history-limit'),
  setChatHistoryLimit: (limit)  => client.put('/api/admin/settings/chat-history-limit', { limit }),

  // 드라이브(NC) 사용자 용량(쿼터)
  listDriveQuotas:    ()           => client.get('/api/admin/drive-quotas'),
  setDriveQuota:      (userId, q)  => client.put(`/api/admin/drive-quotas/${userId}`, { quota: q }),

  // 모델 카탈로그
  listModels:         ()       => client.get('/api/admin/models'),
  getModelCatalog:    ()       => client.get('/api/admin/model-catalog'),
  updateModelCatalog: (id, d)  => client.patch(`/api/admin/model-catalog/${id}`, d),
  approveModel:       (id, d)  => client.post(`/api/admin/model-catalog/${id}/approve`, d || {}),
  deleteModelCatalog: (id)     => client.delete(`/api/admin/model-catalog/${id}`),
  syncModels:         ()       => client.post('/api/admin/model-catalog/sync'),
  // FastAPI 라우트 기능 레지스트리
  listFeatureRegistry:   (params)   => client.get('/api/admin/feature-registry', { params: params || {} }),
  getFeatureRegistryHistory: (id, params) => client.get(`/api/admin/feature-registry/${id}/history`, { params: params || {} }),
  updateFeatureRegistry: (id, data) => client.patch(`/api/admin/feature-registry/${id}`, data),
  reindexFeatureRegistry: ()        => client.post('/api/admin/feature-registry/reindex'),
  // AI 답변 반증(F3) — 시스템 관리자 전용. 스위치 PUT 은 관리자가 화면에서 바꿀 때만 부른다(오늘 비용은 읽기 전용 · 상한 없음).
  getFalsifySettings: ()          => client.get('/api/admin/falsify/settings'),
  setFalsifyMode:     (mode)      => client.put('/api/admin/falsify/mode', { mode }),
  getFalsifyStats:    (days)      => client.get('/api/admin/falsify/stats', { params: { days } }),
};

// 센터별 일일 비용자료(소모품비·인건비·운송료·보관비) — 업로드 보관 + 통합조회.
// 접근 가능한 센터 목록은 **서버가 조직도 기준으로 걸러** 준다(프론트에서 판정하지 않는다).
// 센터 고객사 명부 — 일별 실적의 집계·입력 단위(설계 patch_center_daily_report 단계2)
export const centerShipperAPI = {
  myCenters:      ()                 => client.get('/api/center-shippers/centers'),
  list:           (center, opts = {}) => client.get('/api/center-shippers', { params: { center, q: opts.q || '', only_unmapped: !!opts.only_unmapped } }),
  create:         (body)             => client.post('/api/center-shippers', body),
  update:         (id, body)         => client.patch(`/api/center-shippers/${id}`, body),
  remove:         (id)               => client.delete(`/api/center-shippers/${id}`),
  pwnCandidates:  (q)                => client.get('/api/center-shippers/pwn-candidates', { params: { q: q || '' } }),
  importPreview:  (center, file)     => { const fd = new FormData(); fd.append('file', file); return client.post('/api/center-shippers/import/preview', fd, { params: { center } }); },
  importApply:    (body)             => client.post('/api/center-shippers/import/apply', body),
  // 🔴 <a href> 로 직접 열면 Authorization 헤더가 안 실려 401 이 뜬다 → blob 으로 받아 저장한다
  template:       (center)           => client.get('/api/center-shippers/import/template', { params: { center: center || '' }, responseType: 'blob' }),
};

// 센터 일별 실적 — 입력(근무·도급·수기 출고) + 실적요약(설계 단계3~5)
export const centerDailyAPI = {
  get:          (center, date)  => client.get('/api/center-daily', { params: { center, date } }),
  putLabor:     (body)          => client.put('/api/center-daily/labor', body),
  putShipment:  (body)          => client.put('/api/center-daily/shipment', body),
  getDefault:   (center)        => client.get('/api/center-daily/defaults', { params: { center } }),
  putDefault:   (body)          => client.put('/api/center-daily/defaults', body),
  resync:       (body)          => client.post('/api/center-daily/resync', body),
  report:       (center, date)  => client.get('/api/center-daily/report', { params: { center, date } }),
  // 위와 같은 이유로 blob 으로 받는다(브라우저 네비게이션은 토큰을 안 싣는다)
  exportReport: (center, date)  => client.get('/api/center-daily/report/export', { params: { center, date }, responseType: 'blob' }),
  // 일자 마감·해제({center_key, work_date}) · 그날 수정 이력
  close:        (body)          => client.post('/api/center-daily/close', body),
  reopen:       (body)          => client.post('/api/center-daily/reopen', body),
  history:      (center, date)  => client.get('/api/center-daily/history', { params: { center, date } }),
  // 도급사·구분별 단가(데일리보고 1단계 ②) — 읽기는 센터 권한자, 쓰기는 관리자만(서버 403). 삭제 API 는 없다(«사용 안 함»만)
  contractors:        (center)    => client.get('/api/center-daily/contractors', { params: { center } }),
  addContractor:      (body)      => client.post('/api/center-daily/contractors', body),             // {center_key, name} → 201 {id}
  updateContractor:   (cid, body) => client.patch(`/api/center-daily/contractors/${cid}`, body),     // {name?, active?, sort_order?}
  putRates:           (cid, body) => client.put(`/api/center-daily/contractors/${cid}/rates`, body), // {rates:[{grade, weekday_rate, holiday_rate}]}
  // 그날 도급사 행을 이 몸통으로 통째 바꾼다 — rows 에는 전 도급사 입력을 담는다
  putContractorLabor: (body)      => client.put('/api/center-daily/contractor-labor', body),
};

// 업무 프로젝트(PMS) — 프로젝트·업무·설정·업무 상세·댓글·기록·첨부.
export const taskPmAPI = {
  listProjects:  ()                      => client.get('/api/task-pm/projects'),
  createProject: (body)                  => client.post('/api/task-pm/projects', body),
  getProject:    (id)                    => client.get(`/api/task-pm/projects/${id}`),
  listTasks:     (projectId, params = {}) => client.get(`/api/task-pm/projects/${projectId}/tasks`, { params }),
  // 홈 «내 업무» 보드 — 내가 담당인 업무를 상태 열별로(열마다 per_col 장 · 1~10).
  myTasks:       (params = {})           => client.get('/api/task-pm/my-tasks', { params }),
  projectSummary: (projectId) => client.get(`/api/task-pm/projects/${projectId}/summary`),
  createTask:    (projectId, body)       => client.post(`/api/task-pm/projects/${projectId}/tasks`, body),
  // (J3b) 설정 화면 — 정보·위험 구역·멤버·선택지. 선택지 API 는 상세가 아니라 항목만 돌려준다.
  updateProject:  (id, body)             => client.patch(`/api/task-pm/projects/${id}`, body),
  closeProject:   (id)                   => client.post(`/api/task-pm/projects/${id}/close`),
  reopenProject:  (id)                   => client.post(`/api/task-pm/projects/${id}/reopen`),
  // 본문 있는 DELETE 를 피하려고 서버가 POST 로 받는다(이름을 정확히 다시 입력해야 지워진다).
  deleteProject:  (id, confirmName)      => client.post(`/api/task-pm/projects/${id}/delete`, { confirm_name: confirmName }),
  addMember:      (id, userId, role)     => client.post(`/api/task-pm/projects/${id}/members`, { user_id: userId, role }),
  updateMember:   (id, userId, role)     => client.patch(`/api/task-pm/projects/${id}/members/${userId}`, { role }),
  removeMember:   (id, userId)           => client.delete(`/api/task-pm/projects/${id}/members/${userId}`),
  addOption:      (id, body)             => client.post(`/api/task-pm/projects/${id}/options`, body),
  updateOption:   (optionId, body)       => client.patch(`/api/task-pm/options/${optionId}`, body),
  deleteOption:   (optionId)             => client.delete(`/api/task-pm/options/${optionId}`),
  reorderOptions: (id, kind, ids)        => client.put(`/api/task-pm/projects/${id}/options/order`, { kind, ids }),
  searchUsers:    (q)                    => client.get('/api/task-pm/users/search', { params: { q } }),
  // (J3c1) 업무 상세 — PATCH 는 '보낸 키' 만 바꾼다(null = 비움, 상태는 null 이면 422).
  getTask:          (id)              => client.get(`/api/task-pm/tasks/${id}`),
  updateTask:       (id, body)        => client.patch(`/api/task-pm/tasks/${id}`, body),
  archiveTask:      (id)              => client.post(`/api/task-pm/tasks/${id}/archive`),
  unarchiveTask:    (id)              => client.post(`/api/task-pm/tasks/${id}/unarchive`),
  // 프로젝트 삭제와 같은 이유로 POST 다(본문 있는 DELETE 를 피한다). 성공은 204.
  deleteTask:       (id)              => client.post(`/api/task-pm/tasks/${id}/delete`),
  // 참조자는 집합 통째 교체 — 보낸 목록이 곧 결과다.
  setWatchers:      (id, userIds)     => client.put(`/api/task-pm/tasks/${id}/watchers`, { user_ids: userIds }),
  addChecklist:     (id, text)        => client.post(`/api/task-pm/tasks/${id}/checklist`, { text }),
  // 🔴 항목 수정·삭제는 업무가 아니라 '항목 id' 로 부른다(경로가 /checklist/{item_id} 다).
  updateChecklist:  (itemId, body)    => client.patch(`/api/task-pm/checklist/${itemId}`, body),
  deleteChecklist:  (itemId)          => client.delete(`/api/task-pm/checklist/${itemId}`),
  reorderChecklist: (id, ids)         => client.put(`/api/task-pm/tasks/${id}/checklist/order`, { ids }),
  // (J3c2a) 댓글·기록 — 목록은 오래된 순, 기록은 최신 순이다.
  listComments:   (taskId)              => client.get(`/api/task-pm/tasks/${taskId}/comments`),
  addComment:     (taskId, body)        => client.post(`/api/task-pm/tasks/${taskId}/comments`, body),
  // 🔴 댓글 수정·삭제는 업무가 아니라 '댓글 id' 로 부른다(경로가 /comments/{comment_id} 다).
  //    본문에 mention_user_ids 를 안 넣으면 서버가 기존 멘션을 그대로 둔다(빈 배열은 전부 해제).
  updateComment:  (commentId, body)     => client.patch(`/api/task-pm/comments/${commentId}`, body),
  deleteComment:  (commentId)           => client.delete(`/api/task-pm/comments/${commentId}`),
  listEvents:     (taskId, limit = 100) => client.get(`/api/task-pm/tasks/${taskId}/events`, { params: { limit } }),
  // (J3c2b) 첨부 — 목록은 최신 순 · 올리기는 multipart 필드 'file' · 내려받기는 blob · 지우기 204.
  // 🔴 내려받기·지우기는 업무가 아니라 '첨부 id' 로 부른다(경로가 /attachments/{id} 다).
  listAttachments:    (taskId)       => client.get(`/api/task-pm/tasks/${taskId}/attachments`),
  uploadAttachment:   (taskId, file) => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/api/task-pm/tasks/${taskId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // 🔴 <a href> 로 직접 열면 Authorization 헤더가 안 실려 401 이 뜬다 → blob 으로 받아 저장한다.
  downloadAttachment: (id)           => client.get(`/api/task-pm/attachments/${id}/download`, { responseType: 'blob' }),
  deleteAttachment:   (id)           => client.delete(`/api/task-pm/attachments/${id}`),
  // (J5a) 노트 — 본문은 마크다운 평문(body_md). 목록은 생성 최신 순이고 본문이 없다.
  //    고치기는 늘 base_version 을 보낸다(다른 사람이 먼저 고쳤으면 409).
  listProjectNotes: (projectId, params = {}) => client.get(`/api/task-pm/projects/${projectId}/notes`, { params }),
  listNotes:        (params = {})            => client.get('/api/task-pm/notes', { params }),
  createNote:       (projectId, body)        => client.post(`/api/task-pm/projects/${projectId}/notes`, body),
  getNote:          (id)                     => client.get(`/api/task-pm/notes/${id}`),
  updateNote:       (id, body)               => client.patch(`/api/task-pm/notes/${id}`, body),
  listNoteVersions: (id)                     => client.get(`/api/task-pm/notes/${id}/versions`),
  getNoteVersion:   (id, version)            => client.get(`/api/task-pm/notes/${id}/versions/${version}`),
  duplicateNote:    (id)                     => client.post(`/api/task-pm/notes/${id}/duplicate`),
  deleteNote:       (id)                     => client.delete(`/api/task-pm/notes/${id}`),
  listAutomations:    (projectId)             => client.get(`/api/task-pm/projects/${projectId}/automations`),
  createAutomation:   (projectId, body)       => client.post(`/api/task-pm/projects/${projectId}/automations`, body),
  getAutomation:      (id)                    => client.get(`/api/task-pm/automations/${id}`),
  updateAutomation:   (id, body)              => client.put(`/api/task-pm/automations/${id}`, body),
  setAutomationEnabled: (id, enabled)         => client.patch(`/api/task-pm/automations/${id}/enabled`, { enabled }),
  deleteAutomation:   (id)                    => client.delete(`/api/task-pm/automations/${id}`),
  listAutomationRuns: (id)                    => client.get(`/api/task-pm/automations/${id}/runs`),
  automationChannels: (projectId)             => client.get(`/api/task-pm/projects/${projectId}/automation-channels`),
};

// 월별 SKU ABC 분석 — 집계 실행은 서버의 분석 미러만 사용한다.
export const abcAnalysisAPI = {
  options:   (ym = '', signal) => client.get('/api/abc-analysis/options', { params: { ym: ym || undefined }, signal }),
  list:      (params = {}, signal) => client.get('/api/abc-analysis', { params, signal }),
  preflight: (ym)            => client.get('/api/abc-analysis/preflight', { params: { ym } }),
  runs:      (ym = '')       => client.get('/api/abc-analysis/runs', { params: { ym: ym || undefined } }),
  rerun:     (body)          => client.post('/api/abc-analysis/rerun', body),
  // 링크로 열면 토큰이 안 실려 인증 오류가 난다 → blob 으로 받아 저장한다(일별출고 내려받기와 동일)
  exportExcel: (params = {}) => client.get('/api/abc-analysis/export', { params, responseType: 'blob' }),
  // 재고(과다·장기·악성) 기준값 저장 — 운영·시스템 관리자만(서버가 막는다). 현재 값은 options.stock_rules 로 온다.
  saveStockRules: (body) => client.put('/api/abc-analysis/stock-rules', body),
  // 월간 재고 알림 받는 사람(메신저 사람·채널) — [받는 사람 관리]를 눌렀을 때만 부른다.
  notifyTargets:      ()     => client.get('/api/abc-analysis/notify/targets'),
  addNotifyTarget:    (body) => client.post('/api/abc-analysis/notify/targets', body),
  deleteNotifyTarget: (id)   => client.delete(`/api/abc-analysis/notify/targets/${encodeURIComponent(id)}`),
  notifyUsers:        (q)    => client.get('/api/abc-analysis/notify/users', { params: { q } }),
  notifyChannels:     ()     => client.get('/api/abc-analysis/notify/channels'),
};

// 계약 관리 — 등급은 GET /me 의 can 으로 단추만 숨긴다(막는 것은 서버). 원본은 blob 으로 받는다
// (blob 오류 본문도 Blob 이라 실패 문구는 pages/contracts/contractLabels.errorText 로 읽는다).
export const contractsAPI = {
  me:          ()                => client.get('/api/contracts/me'),
  list:        (params)          => client.get('/api/contracts', { params }),
  departments: ()                => client.get('/api/contracts/departments'),
  get:         (id)              => client.get(`/api/contracts/${id}`),
  file:        (id, disposition) => client.get(`/api/contracts/${id}/file`, { params: { disposition }, responseType: 'blob' }),
  trash:       (id)              => client.delete(`/api/contracts/${id}`),
  // 등록·수정은 multipart(원본 파일 포함) — 수정은 바뀐 칸만 담아 보낸다(키 없음 = 안 바꿈).
  create:      (formData)        => client.post('/api/contracts', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:      (id, formData)    => client.patch(`/api/contracts/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // 휴지통(C2c1) — trash(id) 는 «휴지통으로 보내기»이고 이것은 «휴지통 목록»이다.
  trashList:   ()                => client.get('/api/contracts/trash'),
  // 번호를 줄 때만 본문을 싣는다(본문 없음 = 옛 번호 그대로 복원).
  restore:     (id, contractNo)  => client.post(`/api/contracts/${id}/restore`, contractNo === undefined ? undefined : { contract_no: contractNo }),
  // 확인용 계약번호는 params 로 — axios 가 인코딩한다(문자열로 ?confirm= 를 붙이지 않는다).
  purge:       (id, confirm)     => client.delete(`/api/contracts/${id}/purge`, { params: { confirm } }),
  // 설정(C2c2) — 부서 쓰기는 편집 등급, 편집 권한·전체 이력은 시스템 관리자(막는 것은 서버).
  members:          ()             => client.get('/api/contracts/members'),
  setMember:        (userId, edit) => client.put(`/api/contracts/members/${userId}`, { edit }),
  createDepartment: (name)         => client.post('/api/contracts/departments', { name }),
  // body 는 {name} 또는 {sort_order} 하나만 싣는다(둘을 같이 보내지 않는다).
  updateDepartment: (id, body)     => client.patch(`/api/contracts/departments/${id}`, body),
  deleteDepartment: (id)           => client.delete(`/api/contracts/departments/${id}`),
  // 질의(limit·offset·action)는 params 로 — 경로에 ? 를 손으로 붙이지 않는다.
  events:           (params)       => client.get('/api/contracts/events', { params }),
  // 내보내기(C3) — 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(질의는 category·department·q 만).
  export:           (params)       => client.get('/api/contracts/export', { params, responseType: 'blob' }),
  // 알림(C4) — 시스템 관리자만(막는 것은 서버). 미리보기는 보내지 않는다.
  notifySettings:     ()       => client.get('/api/contracts/notify-settings'),
  saveNotifySettings: (body)   => client.put('/api/contracts/notify-settings', body),
  previewNotify:      (fields) => client.post('/api/contracts/notify-settings/preview', { fields }),
};

// AI 개발실(#2624 ④-S) — 판정·전이는 서버. 오류 본문은 {detail:{reason,message}}(화면은 reason 으로 갈래를 고른다).
// 승인(confirm·replace·specApprove)은 화면 단추를 사람이 누를 때만 부른다(미리보기 틀·앱 메시지로는 부르지 않는다).
export const aidevAPI = {
  tools:        ()             => client.get('/api/aidev/tools'),                              // {tools, month_cost_usd, month_basis}
  createTool:   (name)         => client.post('/api/aidev/tools', { name }),                   // 201 {tool_id}
  tool:         (id)           => client.get(`/api/aidev/tools/${id}`),                        // 세션 상태·대화·확인표
  send:         (id, text)     => client.post(`/api/aidev/tools/${id}/messages`, { text }),    // 202 {accepted}
  stop:         (id)           => client.post(`/api/aidev/tools/${id}/stop`),
  previewOpen:  (id)           => client.post(`/api/aidev/tools/${id}/preview/open`),          // {url, view_id, app_token, token_exp}
  previewToken: (id)           => client.post(`/api/aidev/tools/${id}/preview/token`),         // {app_token, token_exp}
  redeploy:     (id)           => client.post(`/api/aidev/tools/${id}/preview/redeploy`),
  confirm:      (body)         => client.post('/api/aidev/confirm', body),                     // {view_id, item_id?, result_id?}
  humanItem:    (id, body)     => client.post(`/api/aidev/tools/${id}/human-items`, body),
  replace:      (id, body)     => client.post(`/api/aidev/tools/${id}/replace`, body),
  specCard:     (id)           => client.get(`/api/aidev/tools/${id}/spec-card`),              // {version_id, spec, spec_hash}
  specApprove:  (id, body)     => client.post(`/api/aidev/tools/${id}/spec-approve`, body),    // {version_id, spec_hash}
  notify:       (id)           => client.get(`/api/aidev/tools/${id}/notify`),
  archive:      (id)           => client.post(`/api/aidev/tools/${id}/archive`),               // {archived, purge, already?} · 되살림 없음
  // «사용법 묻기»(#3685) — {answer, model} · 대화 저장 없음(직전 문답을 매번 같이 보낸다) ·
  // 120초 = 화면 대기 상한(서버는 nginx /api/ 600초까지 계속 돌아 과금될 수 있음)
  help:         (messages)     => client.post('/api/aidev/help', { messages }, { timeout: 120000 }),
};

// 농심DAS(P-DAS) — 셀 지정은 서버가 한다(P1 §3-3). 오류 본문은 {detail:{code,message}}.
// 스캔은 같은 scan_id 를 서버가 한 번만 넣는다 → 화면은 끊기면 같은 scan_id 로 다시 보낸다.
// 🔴 스캔에는 상한을 둔다 — axios 기본 timeout 은 0(무제한)이라 응답이 안 오면 줄이 영영 멈춘다.
const PDAS_SCAN_TIMEOUT_MS = 8000;
export const pdasAPI = {
  jobs:      (status)              => client.get('/api/pdas/jobs', { params: { status } }),          // {items}
  create:    (body)                => client.post('/api/pdas/jobs', body),                            // 201 {id,…}
  get:       (id)                  => client.get(`/api/pdas/jobs/${id}`),                             // {job,progress,board,ready_cells,last_event_id}
  update:    (id, body)            => client.patch(`/api/pdas/jobs/${id}`, body),
  uploadPlan:(id, file)            => {
    const form = new FormData();
    form.append('file', file);
    return client.post(`/api/pdas/jobs/${id}/plan`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  plan:      (id, params)          => client.get(`/api/pdas/jobs/${id}/plan`, { params }),            // {items,total}
  start:     (id)                  => client.post(`/api/pdas/jobs/${id}/start`),
  scan:      (id, barcode, scanId) => client.post(`/api/pdas/jobs/${id}/scan`, { barcode, scan_id: scanId }, { timeout: PDAS_SCAN_TIMEOUT_MS }),
  remove:    (id, cell, bundleId)  => client.post(`/api/pdas/jobs/${id}/cells/${cell}/remove`, { bundle_id: bundleId }),
  unremove:  (id, cell, bundleId)  => client.post(`/api/pdas/jobs/${id}/cells/${cell}/unremove`, { bundle_id: bundleId }),
  force:     (id, cell, bundleId)  => client.post(`/api/pdas/jobs/${id}/cells/${cell}/force`, { bundle_id: bundleId }),
  undo:      (id, putId)           => client.post(`/api/pdas/jobs/${id}/undo`, { put_id: putId }),
  close:     (id, confirm)         => client.post(`/api/pdas/jobs/${id}/close`, { confirm: !!confirm }),
  // 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(오류 본문도 Blob 이라 pages/pdas/pdasLabels.errorText 로 읽는다).
  template:  ()                    => client.get('/api/pdas/plan-template.xlsx', { responseType: 'blob' }),
  result:    (id)                  => client.get(`/api/pdas/jobs/${id}/result.xlsx`, { responseType: 'blob' }),
};

// 배차 계산기 — 계산은 화면이 하고 서버는 공용 기본값만 든다. 오류 본문은 {detail:{code,message,field?}}
// (422 bad_input · 409 stale). 미리보기(diff)는 쓰지 않는다 · 저장 본문에 base_revision 을 싣는다.
export const dispatchCalcAPI = {
  defaults: ()          => client.get('/api/dispatch-calc/defaults'),                  // {defaults,revision,updated_at,updated_by_name,is_factory,can_edit}
  diff:     (body)      => client.post('/api/dispatch-calc/defaults/diff', body),      // {changes,revision}
  save:     (body)      => client.put('/api/dispatch-calc/defaults', body),            // GET 모양 + changes
  history:  (limit = 50) => client.get('/api/dispatch-calc/defaults/history', { params: { limit } }),   // {items}
};

// 클레임 기준(1단계) — 보기는 메뉴 claim-standards·claim-reasons, 쓰기는 그 보기 키 + claim-admin(서버 require_menu).
// 오류 본문 {detail:{code,message,field?}} (422 bad_input · 404 not_found · 409 duplicate·busy · WMS 503/502/504).
// DELETE 경로는 없다(사용 중지 = active:false). 이력 params = {limit?, before_id?} → {items, next_before_id}.
const claimSeg = encodeURIComponent;
export const claimsAPI = {
  // {comp_rules,cost_items,settings,courier_groups,courier_codes,rule_labels,can_edit,scope} — 인자 없음 = 공통 행만(scope null).
  // params.shipper_id = 그 고객사 범위(예외 행이 있으면 예외 행 · 없으면 공통 행 + scope{registered, has_overrides, standard…}).
  standards:          (params)            => (params ? client.get('/api/claims/standards', { params }) : client.get('/api/claims/standards')),
  standardsHistory:   (params)            => client.get('/api/claims/standards/history', { params }),
  // 고객사 목록 — {items:[{id,display_name,center_key,center_name,temp_zone_hint,standard|null}], total, unregistered_count}.
  shippers:           ()                  => client.get('/api/claims/shippers'),
  createShipperStandard: (body)           => client.post('/api/claims/shipper-standards', body),   // {shipper_id, temp_zone, doc_ref?, reason?}
  patchShipperStandard:  (shipperId, body) => client.patch(`/api/claims/shipper-standards/${claimSeg(shipperId)}`, body),
  // 고객사 예외 기준 만들기 — 그 품온 공통 변상 기준·공통 비용 단가를 복사 → {created:{comp_rules, cost_items}, changed}.
  createShipperOverrides: (shipperId, body) => client.post(`/api/claims/shipper-standards/${claimSeg(shipperId)}/overrides`, body || {}),
  // 화면 [저장] 한 번 — {comp_rules:[{id,…}], cost_items:[{id,…}], settings:{key:value}, reason?} → {changed:{comp_rules,cost_items,settings}}.
  saveStandards:      (body)              => client.put('/api/claims/standards', body),
  // 단가표 — 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(파일 이름은 Content-Disposition filename*).
  exportCostItems:    ()                  => client.get('/api/claims/cost-items/export.xlsx', { responseType: 'blob' }),
  // 단가표 올리기 — multipart 'file' · form 'reason' · dryRun(기본 true) = 미리보기만 → {changes:[{code,name,before,after}], unchanged}.
  importCostItems:    (file, { dryRun = true, reason } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (reason) fd.append('reason', reason);
    return client.post('/api/claims/cost-items/import', fd, { params: { dry_run: dryRun ? 'true' : 'false' } });
  },
  patchCompRule:      (id, body)          => client.patch(`/api/claims/comp-rules/${claimSeg(id)}`, body),
  createCostItem:     (body)              => client.post('/api/claims/cost-items', body),
  patchCostItem:      (id, body)          => client.patch(`/api/claims/cost-items/${claimSeg(id)}`, body),
  putSettings:        (body)              => client.put('/api/claims/settings', body),        // {values:{key:value}, reason?}
  createCourierGroup: (body)              => client.post('/api/claims/courier-groups', body),
  patchCourierGroup:  (code, body)        => client.patch(`/api/claims/courier-groups/${claimSeg(code)}`, body),
  patchCourierCode:   (source, code, body) => client.patch(`/api/claims/courier-codes/${claimSeg(source)}/${claimSeg(code)}`, body),
  syncCouriers:       ()                  => client.post('/api/claims/courier-codes/sync'),   // {sources:[{source,status,…}], unmapped_count}
  approverCheck:      (keyword)           => client.get('/api/claims/approver-check', { params: keyword ? { keyword } : {} }),
  // {items(+v2_links·v1_links),v2_codes,v2_synced_at,rule_groups,no_rule_group_label,v1_codes,v2_categories{코드:분류},categories,can_edit}
  reasons:            ()                  => client.get('/api/claims/reasons'),
  reasonsHistory:     (params)            => client.get('/api/claims/reasons/history', { params }),   // params.key = 사유코드(그 코드 이력만)
  // 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(파일 이름은 Content-Disposition filename*).
  exportReasons:      ()                  => client.get('/api/claims/reasons/export.xlsx', { responseType: 'blob' }),
  putReasonOrder:     (body)              => client.put('/api/claims/reasons/order', body),     // {ids:[전체 id 새 순서], reason?} → {changed, moved}
  createReason:       (body)              => client.post('/api/claims/reasons', body),
  patchReason:        (id, body)          => client.patch(`/api/claims/reasons/${claimSeg(id)}`, body),
  putReasonLinks:     (id, body)          => client.put(`/api/claims/reasons/${claimSeg(id)}/v2-links`, body),   // {codes:[…], reason?}
  syncV2Reasons:      ()                  => client.post('/api/claims/reasons/v2-sync'),     // {fetched,added,updated,missing,synced_at}
  // 1.0 상세사유(게이트웨이 정본 · WMS 동기화 없음) · 2.0 상세사유 분류 · 사유 ↔ 1.0 연결. 지우는 길은 없다(사용 중지 = active:false).
  createV1Code:       (body)              => client.post('/api/claims/reasons/v1-codes', body),   // {code,name,category?,sort_order?,reason?} → {item,changed}
  patchV1Code:        (code, body)        => client.patch(`/api/claims/reasons/v1-codes/${claimSeg(code)}`, body),   // {name?,category?,active?,reason?}
  putV2Category:      (code, body)        => client.put(`/api/claims/reasons/v2-categories/${claimSeg(code)}`, body),   // {category, reason?} → {code,category,changed}
  putReasonV1Links:   (id, body)          => client.put(`/api/claims/reasons/${claimSeg(id)}/v1-links`, body),   // {codes:[…], reason?}
};

// 업무 통합 1단계 · 고객사 기준 정보 — 보기 메뉴 work-customers · 본사 편집 = 보기 + work-customers-edit(서버 require_menu).
// 오류 본문 {detail:{code,message,field?}} (422 bad_input · 403 forbidden · 404 not_found · 409 duplicate·stale).
// 고객사 DELETE 는 없다(종료·복구만) · 수기 이력만 지운다. 검색·센터·담당·빠른 필터는 화면이 거른다(list 는 status 만).
const workSeg = encodeURIComponent;
export const workCustomersAPI = {
  list:              (params)               => client.get('/api/work-customers', { params }),   // {items,total,can_edit,center_options,ops_options,sales_options}
  get:               (id)                   => client.get(`/api/work-customers/${workSeg(id)}`),   // {customer,fields,centers,editable_fields,can_edit,can_center_edit,log_types}
  create:            (body)                 => client.post('/api/work-customers', body),        // 201 {id}
  patchField:        (id, body)             => client.patch(`/api/work-customers/${workSeg(id)}`, body),   // {field,value,before?,reason?} → {changed}
  end:               (id, body)             => client.post(`/api/work-customers/${workSeg(id)}/end`, body || {}),
  restore:           (id, body)             => client.post(`/api/work-customers/${workSeg(id)}/restore`, body || {}),
  shipperCandidates: (id, centerId)         => client.get(`/api/work-customers/${workSeg(id)}/centers/${workSeg(centerId)}/shipper-candidates`),   // {items}
  linkShipper:       (id, centerId, body)   => client.put(`/api/work-customers/${workSeg(id)}/centers/${workSeg(centerId)}/shipper`, body),   // {shipper_id|null, reason?}
  history:           (id, params)           => client.get(`/api/work-customers/${workSeg(id)}/history`, { params }),   // params {category?, q?} → {items, categories}
  logs:              (id, params)           => client.get(`/api/work-customers/${workSeg(id)}/logs`, { params }),      // params {type?, q?} → {items, count, amount_sum}
  createLog:         (id, body)             => client.post(`/api/work-customers/${workSeg(id)}/logs`, body),           // {log_date, log_type, content, amount?} → 201 {id}
  deleteLog:         (id, logId)            => client.delete(`/api/work-customers/${workSeg(id)}/logs/${workSeg(logId)}`),
};

// 클레임 등록(단건 · 2단계) — 메뉴 claim-register(서버 require_menu). 오류 본문 {detail:{code,message,field}}
// (422 bad_input·blocked · 404 not_found · 409 duplicate·not_editable·closed). DELETE 메서드는 없다 —
// 임시저장 삭제 = POST …/discard · 사진 지우기 = POST …/photos/{photo_id}/remove(deleted_at 만). claimsAPI 와 섞지 않는다.
export const claimLedgerAPI = {
  form:        ()             => client.get('/api/claims/ledger/form'),     // {reasons,centers,shippers,pack_items,evidence,vat_pct,rule_labels,my_center_keys,v2_types_unset}
  today:       ()             => client.get('/api/claims/ledger/today'),    // {scope, center_keys, items}
  lookup:      (invoice)      => client.get('/api/claims/ledger/lookup', { params: { invoice } }),   // {found, source, sources, shipment(invoice_count·order_count), items, v2_claims, v1_claims, reship_of, warnings, …}
  preview:     (body)         => client.post('/api/claims/ledger/preview', body),   // 쓰지 않는다 — {calc, deadlines, checks, suggestions, approval}
  create:      (body)         => client.post('/api/claims/ledger', body),   // body.action = draft | register
  get:         (id)           => client.get(`/api/claims/ledger/${claimSeg(id)}`),
  update:      (id, body)     => client.put(`/api/claims/ledger/${claimSeg(id)}`, body),
  discard:     (id)           => client.post(`/api/claims/ledger/${claimSeg(id)}/discard`),
  uploadPhoto: (id, file)     => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/api/claims/ledger/${claimSeg(id)}/photos`, fd);
  },
  // 링크로 열면 토큰이 안 실린다 → blob 으로 받는다.
  photo:       (id, photoId)  => client.get(`/api/claims/ledger/${claimSeg(id)}/photos/${claimSeg(photoId)}`, { responseType: 'blob' }),
  removePhoto: (id, photoId)  => client.post(`/api/claims/ledger/${claimSeg(id)}/photos/${claimSeg(photoId)}/remove`),
};

// 클레임 다건등록(엑셀 · 구성안 화면②) — 메뉴 claim-register(단건과 같은 키 · 서버 require_menu). 오류 본문 {detail:{code,message,field}}
// (422 bad_input·too_many_rows · 404 not_found · 409 busy·not_editable·duplicate). 업로드·등록은 202 → GET /{id} 로 진행률.
// DELETE 메서드는 없다 — 취소 = POST …/cancel. 사진 지우기·보기는 claimLedgerAPI.removePhoto·photo 에 행의 claim_id 를 넘긴다.
const claimBulkPath = (id) => `/api/claims/bulk/${claimSeg(id)}`;
export const claimBulkAPI = {
  list:        ()                      => client.get('/api/claims/bulk'),   // {items:[{id,file_name,status,total_rows,counts,register,created_by_name,created_at,…}]}
  // 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(파일 이름은 Content-Disposition filename*).
  template:    ()                      => client.get('/api/claims/bulk/template.xlsx', { responseType: 'blob' }),
  upload:      (file)                  => {                                 // multipart 'file' → 202 {upload_id, status}
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/api/claims/bulk', fd);
  },
  get:         (id)                    => client.get(claimBulkPath(id)),    // 진행률 · counts · register · evidence · selection · can_*
  rows:        (id, params)            => client.get(`${claimBulkPath(id)}/rows`, { params }),   // params {level?, offset, limit} → {total, offset, limit, items}
  row:         (id, rowId)             => client.get(`${claimBulkPath(id)}/rows/${claimSeg(rowId)}`),   // 스냅샷 · 본문 · 미리보기 · 사진 · prefill · 2.0 후보
  patchRow:    (id, rowId, edits)      => client.patch(`${claimBulkPath(id)}/rows/${claimSeg(rowId)}`, { edits }),   // null = 자동 값으로
  select:      (id, rowIds, selected)  => client.post(`${claimBulkPath(id)}/select`, { row_ids: rowIds, selected: !!selected }),
  uploadPhoto: (id, rowId, file)       => {                                 // 첫 사진이면 서버가 임시저장 원장을 만든다 → {photo, claim_id, row}
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`${claimBulkPath(id)}/rows/${claimSeg(rowId)}/photos`, fd);
  },
  errors:      (id)                    => client.get(`${claimBulkPath(id)}/errors.xlsx`, { responseType: 'blob' }),
  revalidate:  (id)                    => client.post(`${claimBulkPath(id)}/revalidate`),   // 202
  register:    (id, rowIds)            => client.post(`${claimBulkPath(id)}/register`, rowIds ? { row_ids: rowIds } : {}),   // 202 · 없으면 고른 행 전부
  save:        (id)                    => client.post(`${claimBulkPath(id)}/save`),
  cancel:      (id)                    => client.post(`${claimBulkPath(id)}/cancel`),
};

// 네이버 CAPA — 라우터 전체 require_menu("capa"). 오류 본문 {detail:{code,message,field?}} · 503 storage_not_ready.
// 관리 API(settings·putSettings·enable·disable·putCredentials·audit) — 쓰기 4종은 서버가 admin 만 받는다(get_current_admin)
// · 관리 탭을 눌렀을 때만 부른다.
// 알림 대상 API — 목록 GET 은 ops 도 · 나머지는 admin 만(get_current_admin) · 관리 탭을 눌렀을 때만 부른다.
export const capaAPI = {
  status:    ()            => client.get('/api/capa/status'),
  latest:    ()            => client.get('/api/capa/latest'),
  snapshots: (date)        => client.get('/api/capa/snapshots', { params: date ? { date } : {} }),
  snapshotRows: (id)       => client.get(`/api/capa/snapshots/${id}/rows`),
  events:    (date)        => client.get('/api/capa/events', { params: date ? { date } : {} }),
  // 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(오류 본문도 Blob 이라 화면이 읽어 code 를 꺼낸다).
  exportCsv: (start, end)  => client.get('/api/capa/export.csv', { params: { start, end }, responseType: 'blob' }),
  settings:       ()                  => client.get('/api/capa/settings'),
  putSettings:    (changes)           => client.put('/api/capa/settings', changes),
  enable:         (reason)            => client.post('/api/capa/enable', { reason }),
  disable:        (reason)            => client.post('/api/capa/disable', reason ? { reason } : {}),
  // 🔴 계정 값은 본문으로만(쿼리스트링 금지 — 우산 §10-1 ①) · 20초 시한(응답이 안 와도 화면이 칸을 비운다).
  putCredentials: (login_id, password) => client.put('/api/capa/credentials', { login_id, password }, { timeout: 20000 }),
  audit:          (limit)             => client.get('/api/capa/audit', { params: limit ? { limit } : {} }),
  notifyTargets:      ()            => client.get('/api/capa/notify/targets'),                  // 배열 · 최신순
  addNotifyTarget:    (body)        => client.post('/api/capa/notify/targets', body),           // 201 · 대상 한 개
  patchNotifyTarget:  (id, changes) => client.patch(`/api/capa/notify/targets/${id}`, changes), // 대상 한 개
  deleteNotifyTarget: (id)          => client.delete(`/api/capa/notify/targets/${id}`),         // {ok, id}
  testNotifyTarget:   (id)          => client.post(`/api/capa/notify/targets/${id}/test`),      // {ok, code} · 성공 code='ok'
  notifyUsers:        (q)           => client.get('/api/capa/notify/users', { params: { q } }), // {items:[{id,name,email_hint}]}
  notifyChannels:     ()            => client.get('/api/capa/notify/channels'),                 // {enabled, items:[{id,name,team,type,label}]}
};

// 입고 CAPA 모니터링 — 라우터 전체 require_menu("inbound-capa"). 오류 본문 {detail:"문구"}.
// 🔴 refresh 는 응답 본문을 그대로 돌려준다 — {run, cached, cooldown_remaining_s}(cached=true 면 직전 판정 · 운영 DB 0회)
//    · 다른 판정이 진행 중이면 409(detail = 서버 문구). 화면이 cached·409 를 안내한다.
// 엑셀(export·SKU 양식·SKU 내보내기)은 링크로 열면 토큰이 안 실린다 → blob 으로 받는다.
export const inboundCapaAPI = {
  me:       ()                      => client.get('/api/inbound-capa/me'),
  view:     (date)                  => client.get('/api/inbound-capa/view', { params: date ? { date } : {} }),
  refresh:  ()                      => client.post('/api/inbound-capa/refresh'),
  detail:   (unitId, date)          => client.get('/api/inbound-capa/detail', { params: { unit_id: unitId, date } }),
  exportXlsx: (date, unitId)        => client.get('/api/inbound-capa/export', {
    params: { ...(date ? { date } : {}), ...(unitId ? { unit_id: unitId } : {}) }, responseType: 'blob',
  }),
  alerts:   (limit)                 => client.get('/api/inbound-capa/alerts', { params: limit ? { limit } : {} }),
  units:          ()                => client.get('/api/inbound-capa/units'),
  createUnit:     (body)            => client.post('/api/inbound-capa/units', body),
  updateUnit:     (id, changes)     => client.patch(`/api/inbound-capa/units/${id}`, changes),
  deleteUnit:     (id)              => client.delete(`/api/inbound-capa/units/${id}`),
  addException:   (unitId, body)    => client.post(`/api/inbound-capa/units/${unitId}/exceptions`, body),
  deleteException:(unitId, exId)    => client.delete(`/api/inbound-capa/units/${unitId}/exceptions/${exId}`),
  settings:       ()                => client.get('/api/inbound-capa/settings'),
  putSettings:    (changes)         => client.put('/api/inbound-capa/settings', changes),
  orgs:           ()                => client.get('/api/inbound-capa/settings/orgs'),
  putOrgs:        (body)            => client.put('/api/inbound-capa/settings/orgs', body),
  notifyChannels: ()                => client.get('/api/inbound-capa/notify/channels'),
  notifyUsers:    (q)               => client.get('/api/inbound-capa/notify/users', { params: { q } }),
  notifyTest:     ()                => client.post('/api/inbound-capa/notify/test'),
  skuTemplate:    ()                => client.get('/api/inbound-capa/skus/template', { responseType: 'blob' }),
  skuExport:      ()                => client.get('/api/inbound-capa/skus/export', { responseType: 'blob' }),
  skuUnregistered:()                => client.get('/api/inbound-capa/skus/unregistered'),
  skuImport:      (file)            => {
    const form = new FormData();
    form.append('file', file);
    return client.post('/api/inbound-capa/skus/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  skus:           (params = {})     => client.get('/api/inbound-capa/skus', { params }),
  createSku:      (body)            => client.post('/api/inbound-capa/skus', body),
  updateSku:      (id, changes)     => client.patch(`/api/inbound-capa/skus/${id}`, changes),
  deleteSku:      (id)              => client.delete(`/api/inbound-capa/skus/${id}`),
  // 17시 마감 기록(입고예정) — center 가 비면 볼 수 있는 센터 전부, date 가 비면 서버 기본 날짜(마감 이후면 내일)
  close:          (center, date)    => client.get('/api/inbound-capa/close', { params: { center: center || '', date: date || '' } }),
  putClose:       (body)            => client.put('/api/inbound-capa/close', body),          // {center_key, plan_date, rows}
  resyncClose:    (body)            => client.post('/api/inbound-capa/close/resync', body),  // {center_key, plan_date}
  exportClose:    (center, date)    => client.get('/api/inbound-capa/close/export', {
    params: { center: center || '', date: date || '' }, responseType: 'blob',
  }),
};

// 개발요청 — 막는 것은 서버(메뉴·부서·AX). 비공개 요청은 서버가 가려서 준다(masked).
// 쓰기(D4b) — 고치기는 바뀐 칸만 담는다(키 없음 = 안 바꿈) · 공개 범위(visibility)는 몸통에 넣지 않는다(서버 400).
// 첨부는 하나씩 multipart · 내려받기는 blob(오류 본문도 Blob 이라 pages/devreq/devreqLabels.errorText 로 읽는다).
export const devreqAPI = {
  me:          ()       => client.get('/api/devreq/me'),
  rounds:      ()       => client.get('/api/devreq/rounds'),
  departments: ()       => client.get('/api/devreq/departments'),
  list:        (params) => client.get('/api/devreq/requests', { params }),
  get:         (id)     => client.get(`/api/devreq/requests/${id}`),
  create:        (body)          => client.post('/api/devreq/requests', body),
  update:        (id, body)      => client.patch(`/api/devreq/requests/${id}`, body),
  rank:          (body)          => client.put('/api/devreq/requests/rank', body),
  withdraw:      (id)            => client.post(`/api/devreq/requests/${id}/withdraw`),
  confirm:       (id, body)      => client.post(`/api/devreq/requests/${id}/confirm`, body),
  files:         (id)            => client.get(`/api/devreq/requests/${id}/files`),
  upload:        (id, formData)  => client.post(`/api/devreq/requests/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download:      (fileId)        => client.get(`/api/devreq/files/${fileId}`, { responseType: 'blob' }),
  removeFile:    (fileId)        => client.delete(`/api/devreq/files/${fileId}`),
  comments:      (id)            => client.get(`/api/devreq/requests/${id}/comments`),
  addComment:    (id, body)      => client.post(`/api/devreq/requests/${id}/comments`, body),
  removeComment: (commentId)     => client.delete(`/api/devreq/comments/${commentId}`),
  // D5a — AX 검토 칸 · 일괄 상태 · 공개 전환 · 요청자 연결(서버는 전부 AX 만).
  review:        (id, body)       => client.post(`/api/devreq/requests/${id}/review`, body),
  bulkStatus:    (ids, status)    => client.post('/api/devreq/requests/bulk-status', { ids, status }),
  setVisibility: (id, visibility) => client.post(`/api/devreq/requests/${id}/visibility`, { visibility }),
  setRequester:  (id, userId)     => client.post(`/api/devreq/requests/${id}/requester`, { user_id: userId }),
  // AX실 담당자 — 후보 = 검토자 명단(서버 _assignee_candidates) · userId null = 해제.
  assigneeCandidates: ()           => client.get('/api/devreq/assignee-candidates'),
  setAssignee:   (id, userId)     => client.post(`/api/devreq/requests/${id}/assignee`, { user_id: userId }),
  searchUsers:   (q)              => client.get('/api/devreq/users/search', { params: { q } }),
  // 2판 V3 — 추가 요청(보내기·거두기 = AX · 답 = 부서 작성자·요청자). 보내기는 201, 답·거두기는 200.
  ask:           (id, body)       => client.post(`/api/devreq/requests/${id}/asks`, { body }),
  answerAsk:     (askId, answer)  => client.post(`/api/devreq/asks/${askId}/answer`, { answer }),
  cancelAsk:     (askId)          => client.post(`/api/devreq/asks/${askId}/cancel`),
  // D5b — 현황판(메뉴 권한 전원) · 회차·참여 부서·검토자 관리(서버는 쓰기를 AX 만 · 검토자 지정은 시스템 관리자만).
  dashboard:       (params)          => client.get('/api/devreq/dashboard', { params }),
  createRound:     (body)            => client.post('/api/devreq/rounds', body),
  updateRound:     (id, body)        => client.patch(`/api/devreq/rounds/${id}`, body),
  transitionRound: (id, to, confirm) => client.post(`/api/devreq/rounds/${id}/transition`, { to, confirm }),
  deleteRound:     (id)              => client.delete(`/api/devreq/rounds/${id}`),   // D8 — 요청이 있으면(철회 포함) 서버가 409
  closePreview:    (id)              => client.get(`/api/devreq/rounds/${id}/close-preview`),
  departmentsAll:  ()                => client.get('/api/devreq/departments', { params: { all: 1 } }),   // 회차 관리의 부서 표 전용(비활성 포함 · AX 만 — 서버 list_departments)
  createDept:      (body)            => client.post('/api/devreq/departments', body),
  updateDept:      (id, body)        => client.patch(`/api/devreq/departments/${id}`, body),
  daouOrgs:        (q)               => client.get('/api/devreq/daou-orgs', { params: { q } }),
  staff:           ()                => client.get('/api/devreq/staff'),
  setStaff:        (userId, staff)   => client.put(`/api/devreq/staff/${userId}`, { staff }),
  // DEV 확인 계정(서버는 전부 AX 만) — 발급 전이면 GET 이 null · 발급·재발급 응답에 비밀번호 없음 · 회수는 상태 조건 없음(발급 기록 없으면 404).
  // 발급은 화면이 본 revision 을 expected_revision 으로 늘 보낸다(어긋나면 409) — 인자를 빼면 null(=행 없음을 봤다)로 간다 · 재발급은 409 로 닫힌다. 재발급하려면 본 revision 을 넘긴다.
  // menuKeys = «확인할 메뉴»(그 DEV 계정 menu_access 에 true 로) — 빼면 빈 목록(지금처럼 요청자 권한 복사만). 후보는 qaMenus(발급자 기준).
  qaAccount:       (id)              => client.get(`/api/devreq/requests/${id}/qa-account`),
  qaMenus:         ()                => client.get('/api/devreq/qa-menus'),
  issueQaAccount:  (id, expectedRevision, menuKeys) => client.post(`/api/devreq/requests/${id}/qa-account`, {
    expected_revision: expectedRevision ?? null, menu_keys: Array.isArray(menuKeys) ? menuKeys : [],
  }),
  revokeQaAccount: (id)              => client.delete(`/api/devreq/requests/${id}/qa-account`),
  // AI 종합 판단(P2a · 서버는 전부 AX 만) — 판단 기준 GET · 저장 PUT {criteria}(빈 글 = 기본 기준으로 · 4,000자 넘으면 400 · 응답은 GET 과 같은 꼴) · 요청별 판단 GET.
  aiCriteria:      ()                => client.get('/api/devreq/ai-criteria'),
  saveAiCriteria:  (criteria)        => client.put('/api/devreq/ai-criteria', { criteria }),
  aiReport:        (id)              => client.get(`/api/devreq/requests/${id}/ai-report`),
};

export const centerCostAPI = {
  myCenters:  ()                    => client.get('/api/center-costs/centers'),
  list:       (center, start, end)  => client.get('/api/center-costs', { params: { center, start: start || '', end: end || '' } }),
  supplies:   (centerToken, opts = {}) => {
    const params = new URLSearchParams();
    params.append('center_token', centerToken);
    params.append('start', opts.start || '');
    params.append('end', opts.end || '');
    params.append('group_by', opts.groupBy || 'vendor');
    (opts.vendors || []).forEach((vendor) => params.append('vendor', vendor));
    if (opts.detailKey !== undefined && opts.detailKey !== null) params.append('detail_key', opts.detailKey);
    if (opts.detailOffset !== undefined) params.append('detail_offset', String(opts.detailOffset));
    if (opts.detailLimit !== undefined) params.append('detail_limit', String(opts.detailLimit));
    return client.get('/api/center-costs/supplies', { params });
  },
  create:     (formData)            => client.post('/api/center-costs', formData),
  remove:     (id)                  => client.delete(`/api/center-costs/${id}`),
  summary:    (start, end)          => client.get('/api/center-costs/summary', { params: { start: start || '', end: end || '' } }),
  downloadUrl: (fileId)             => `/api/center-costs/files/${fileId}/download`,
  download:   (fileId)              => client.get(`/api/center-costs/files/${fileId}/download`, { responseType: 'blob' }),
};

// 센터 실적 대시보드(일별출고 엑셀 업로드·저장·조회) — 권한 판정은 서버 /center-perf/me 하나(menu_registry 밖).
// center 인자 = 실적 센터명(BPO 이름 · /center-perf/me 의 token). 업로드는 FormData(center, file).
// 저장(save)도 같은 파일을 다시 보낸다 — 서버가 다시 파싱·검산한다(미리보기 결과를 믿지 않는다).
// save 의 confirms = 미리보기 확인 경고 중 체크한 코드 — 있으면 FormData 'confirm' 에 JSON 배열로 넣는다(서버가 다시 계산해 빠지면 409).
// splitRules/saveSplitRule/removeSplitRule = 고객사별 실적 센터 예외(추가·삭제는 서버가 관리 권한을 다시 본다).
// ref* = 기준 데이터(실적 센터·별칭·수기값) — 판정은 서버가 한다(관리 조직 = 순서·소계·수기값 · 운영·시스템 관리자 = 권한 센터·사용·별칭·추가).
// bpo* = BPO 엑셀 가져오기(미리보기 → 체크한 항목만 적용 · 서버가 같은 파일을 다시 파싱) · refTargets/refCustomers/refLegacy = 그 조회.
// refUnlinked/refLinks/saveLink/removeLink = 고객사명 연결(업로드 원문 → 고객사 마스터 표준명 · 연결·해제는 서버가 관리 권한을 다시 본다)
// · refDaily = 일별 출고(날짜마다 업로드가 있으면 업로드만, 없으면 과거 출고만). center 를 비우면 조회 범위 전부.
const perfForm = (center, file, confirms) => {
  const fd = new FormData();
  fd.append('center', center);
  fd.append('file', file);
  if (confirms && confirms.length) fd.append('confirm', JSON.stringify(confirms));
  return fd;
};
const bpoForm = (file, items) => {
  const fd = new FormData();
  fd.append('file', file);
  if (items) fd.append('items', JSON.stringify(items));
  return fd;
};
export const centerPerfAPI = {
  // 🔴 사이드바가 화면마다 저절로 부르는 요청이라 401 이어도 로그아웃하지 않는다(QUIET_401).
  me:           ()                  => client.get('/api/center-perf/me', { [QUIET_401]: true }),
  preview:      (center, file)      => client.post('/api/center-perf/preview', perfForm(center, file)),
  save:         (center, file, confirms = []) => client.post('/api/center-perf/uploads', perfForm(center, file, confirms)),
  uploads:      (center)            => client.get('/api/center-perf/uploads', { params: { center } }),
  removeUpload: (id)                => client.delete(`/api/center-perf/uploads/${id}`),
  data:         (center, opts = {}) => client.get('/api/center-perf/data', { params: { center, start: opts.start || '', end: opts.end || '' } }),
  adminGetViewOrgs:  ()             => client.get('/api/admin/center-perf/view-orgs'),
  adminSaveViewOrgs: (orgs)         => client.put('/api/admin/center-perf/view-orgs', { orgs }),
  adminGetManageOrgs:  ()           => client.get('/api/admin/center-perf/manage-orgs'),
  adminSaveManageOrgs: (orgs)       => client.put('/api/admin/center-perf/manage-orgs', { orgs }),
  refCenters:   ()                  => client.get('/api/center-perf/ref/centers'),
  createCenter: (body)              => client.post('/api/center-perf/ref/centers', body),
  updateCenter: (id, patch)         => client.patch(`/api/center-perf/ref/centers/${id}`, patch),
  addAlias:     (id, alias)         => client.post(`/api/center-perf/ref/centers/${id}/aliases`, { alias }),
  removeAlias:  (id, aliasId)       => client.delete(`/api/center-perf/ref/centers/${id}/aliases/${aliasId}`),
  relink:       ()                  => client.post('/api/center-perf/ref/centers/relink'),
  bpoPreview:   (file)              => client.post('/api/center-perf/bpo/preview', bpoForm(file)),
  bpoApply:     (file, items)       => client.post('/api/center-perf/bpo/apply', bpoForm(file, items)),
  bpoImports:   ()                  => client.get('/api/center-perf/bpo/imports'),
  // 연도를 빼면(undefined) 서버가 저장된 가장 최근 연도를 고른다.
  refTargets:   (year)              => client.get('/api/center-perf/bpo/targets', { params: { year } }),
  refCustomers: (year, q = '')      => client.get('/api/center-perf/bpo/customers', { params: { year, q } }),
  refLegacy:    (center, opts = {}) => client.get('/api/center-perf/bpo/legacy', { params: { center, start: opts.start || '', end: opts.end || '' } }),
  refUnlinked:  (center = '')       => client.get('/api/center-perf/names/unlinked', { params: { center } }),
  refLinks:     (center = '')       => client.get('/api/center-perf/names/links', { params: { center } }),
  saveLink:     (body)              => client.post('/api/center-perf/names/links', body),
  removeLink:   (id)                => client.delete(`/api/center-perf/names/links/${id}`),
  refDaily:     (center, opts = {}) => client.get('/api/center-perf/names/daily', { params: { center, start: opts.start || '', end: opts.end || '' } }),
  splitRules:      (center = '')    => client.get('/api/center-perf/splits/rules', { params: { center } }),
  saveSplitRule:   (body)           => client.post('/api/center-perf/splits/rules', body),
  removeSplitRule: (id)             => client.delete(`/api/center-perf/splits/rules/${id}`),
  // 날짜별 가중치 — month = 'YYYY-MM'(조회는 빼면 서버가 KST 이번 달). 저장은 바뀐 날만 {days:[{date, 다섯 칸}]}.
  weights:         (month = '')     => client.get('/api/center-perf/weights', { params: { month } }),
  saveWeights:     (month, days)    => client.put('/api/center-perf/weights', { days }, { params: { month } }),
  clearWeights:    (month)          => client.delete('/api/center-perf/weights', { params: { month } }),
  saveDowDefaults: (values)         => client.put('/api/center-perf/weights/dow-defaults', { values }),
  // 대시보드(센터실적 표) — date = 'YYYY-MM-DD'(빈 값이면 서버가 어제 KST) · source = 'upload_first' | 'legacy'.
  // dashExport 는 같은 인자로 화면 표와 같은 엑셀(blob)을 받는다.
  dashSummary:     (date = '', source = 'upload_first') => client.get('/api/center-perf/dash/summary', { params: { date, source } }),
  dashExport:      (date = '', source = 'upload_first') => client.get('/api/center-perf/dash/export', { params: { date, source }, responseType: 'blob' }),
  // 대시보드 상수 한 칸 바로 입력 — body = {kind: center|subtotal|total, name, field: max_perf|std_capa|storage_capa|current_storage,
  // value(빈 값 null = 비움), as_of?(현보관량 기준일)}. 권한(관리자·기준 데이터 관리 조직)은 서버가 다시 본다.
  dashSaveConstant: (body)          => client.put('/api/center-perf/dash/constants', body),
};

// 사이드바 메뉴 설정(순서·그룹·이름·숨김 · app_settings 'sidebar_menu') — get 은 로그인 사용자 전원, admin* 은 시스템 관리자.
// *Favorite* = 내 메뉴 즐겨찾기(user_menu_favorites · 사용자별). toggle 은 한 항목 켜기/끄기.
export const sidebarMenuAPI = {
  get:        ()        => client.get('/api/sidebar-menu'),
  adminGet:   ()        => client.get('/api/admin/sidebar-menu'),
  adminSave:  (entries) => client.put('/api/admin/sidebar-menu', { entries }),
  adminReset: ()        => client.delete('/api/admin/sidebar-menu'),
  // 🔴 화면을 열 때마다 저절로 나가는 요청이라 401 이어도 로그아웃하지 않는다(QUIET_401).
  getFavorites: ()      => client.get('/api/sidebar-menu/favorites', { [QUIET_401]: true }),
  // 🔴 상한 필수 — 응답이 영영 안 오면 나가 있는 토글 수가 안 줄어 그 탭의 즐겨찾기 반영이 전부 막힌다.
  toggleFavorite: (key, on) => client.post('/api/sidebar-menu/favorites/toggle', { key, on }, { timeout: 10000 }),
};

// 푸시 구독 기기 종류 — PC 라는 증거(마우스 호버·정밀 포인터·모바일 UA 아님)가 모두 있을 때만 'desktop', 그 밖은 'mobile', 모르면 null.
// 서버는 PC 구독에 메신저 푸시를 짧게 보관해 꺼져 있던 PC 가 켜질 때 밀린 알림이 몰려오지 않는다(2026-10-18).
// 🔴 폰을 PC 로 잡으면 폰이 끊긴 동안 온 멘션이 안 온다 — 애매하면 'mobile'. 화면 폭·«모바일 보기» 토글과 무관(좁은 PC 창도 PC).
const MOBILE_UA_RE = /Android|iPhone|iPad|iPod|Mobile/i;   // backend/routers/push.py _MOBILE_UA 와 쌍둥이
export function pushDeviceKind() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    const nav = window.navigator || {};
    const pcPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const uaMobile = (nav.userAgentData && nav.userAgentData.mobile === true) || MOBILE_UA_RE.test(nav.userAgent || '');
    return pcPointer && !uaMobile ? 'desktop' : 'mobile';
  } catch (e) {
    return null;
  }
}

export const pushAPI = {
  getVapidKey: ()     => client.get('/api/push/vapid-public-key'),
  subscribe:   (sub)  => {
    const json = sub && typeof sub.toJSON === 'function' ? sub.toJSON() : sub;   // 브라우저 구독 객체를 그대로 받아도 키가 사라지지 않게
    return client.post('/api/push/subscribe', { ...json, device_kind: pushDeviceKind() });
  },
  unsubscribe: (ep)   => client.post('/api/push/unsubscribe', { endpoint: ep }),
  test:        ()     => client.post('/api/push/test'),
  // 메신저 화면을 보는 중이라는 신호(messengerOnScreen.js 하트비트) — 보는 중엔 서버가 메신저 푸시를 건너뛴다.
  messengerViewing: (viewing) => client.post('/api/push/messenger-viewing', { viewing: !!viewing }),
  // 이 기기에서 게이트웨이를 쓰는 중이라는 신호(deviceActivity.js 하트비트) — 쓰는 기기엔 바로, 나머지는 미뤘다가 보낸다.
  // 🔴 20초 백그라운드 신호라 401 이 와도 로그아웃하지 않는다(QUIET_401).
  active:      (endpoint) => client.post('/api/push/active', { endpoint: endpoint || null }, { [QUIET_401]: true }),
  // 서비스워커 push 고리 기록 올리기(deviceActivity.js uploadSwRing) — 앱을 열 때 저절로 나가는 요청이라 401 이어도 로그아웃하지 않는다.
  swRing:      (body) => client.post('/api/push/sw-ring', body, { [QUIET_401]: true }),
  // PC 사용 중 폰 메신저 알림 지연(초 · 0=바로) — 내정보.
  getPrefs:    ()     => client.get('/api/push/prefs'),
  setPrefs:    (sec)  => client.put('/api/push/prefs', { mobile_delay_sec: sec }),
};
